// ============================================================
// Bayside Hub - mobile app
// Rebuild 2026-09-14. Replaces the swipe-deck / merged-chat build.
//
// WHY THIS EXISTS
// Uptake was low for two reasons: it was clunky on a phone, and it
// answered from the wrong scope because /route guessed which engine
// to use. The guess is GONE. The user picks the tool, then asks, and
// the tool is shown in the chat header the whole time.
//
// HARD RULE: no auto-routing, ever. Do not reintroduce a single
// "ask anything" bar, an intent classifier, or MERGED_QA_IDS.
// Every request carries an explicit tool. If you cannot say which
// tool a question belongs to, the user has not picked one yet.
//
// React + ReactDOM are CDN globals (SRI-locked in the HTML shell).
// Built by build-html.bat -> esbuild -> bayside-assistant.js
// ============================================================

const WORKER_URL        = "https://blue-snowflake-b042.ryanamcc88.workers.dev";
const SESSION_TOKEN_KEY = "bayside-session-token";
const LAST_NAME_KEY     = "bayside-last-name";
const OUTBOX_KEY        = "bayside-outbox-v1";
const THREADS_KEY       = "bayside-threads-v1";

// Calver. Bump on every user-visible change (see CLAUDE.md).
const APP_VERSION = "2026.10.15";
// The version live on Pages, read from /version.json (written by
// bayside-ship.bat on every ship). Blank until the first check lands.
// Module-level so every bug report can say whether the sender is behind.
let LIVE_VERSION = "";

// Bayside Electrical Design System
const C = {
  navy:    "#002B60",
  deep:    "#00253F",
  orange:  "#F15522",
  white:   "#FFFFFF",
  off:     "#F7F6F3",
  line:    "#E8E8E8",
  mid:     "#9A9A9A",
  char:    "#2A2A2A",
  body:    "#595959",
  success: "#1F8E4A",
  warning: "#E89A1C",
  danger:  "#C0392B",
  gold:    "#B4770F",
  sea:     "#1E6E8C",
};

const EASE = "cubic-bezier(.2,0,0,1)";

// ============================================================
// TOOLS - the whole routing table. Five scopes, picked by hand.
// `endpoint` and `mode` are the wiring; nothing infers them.
// ============================================================
const TOOLS = {
  regs: {
    key: "regs",
    title: "Regulations",
    scope: "AS/NZS 3000 - NZ ECP",
    colour: C.navy,
    endpoint: "/regs",
    mode: "sse",
    needsCite: true,
    blurb: "AS/NZS 3000 and the NZ rules, answered in plain words with the clause.",
    cta: "Ask about the regs",
    placeholder: "Ask about the regs...",
    greet: "Righto, regs mode. Ask away and I'll give you the clause it comes from. Add a photo of the setup if it helps.",
    photo: true,
    chips: ["RCD on a shared circuit", "Min cable size for a 32A oven", "Clearance to a shower"],
  },
  tech: {
    key: "tech",
    title: "Tech support",
    scope: "Faults - fittings - gear",
    colour: C.orange,
    endpoint: "/howdoi",
    mode: "sse",
    needsCite: false,
    blurb: "Stuck on a fault or an unfamiliar fitting? Talk it through on site.",
    cta: "Ask tech support",
    placeholder: "What have you got in front of you?",
    // 15 Oct 2026: the job number lets the worker pull what we already know
    // about that site (resolveTechSite). Without one it uses the Fergus calendar.
    greet: "What have you got in front of you? Tell me the gear, what it's doing and what you've tried, or snap a photo of it. Give me the job number too and I'll check what we already know about that site.",
    photo: true,
    chips: ["Breaker won't reset", "Heat pump E5 fault", "Starlink no signal"],
  },
  job: {
    key: "job",
    title: "Job knowledge",
    scope: "Sites - clients - past jobs",
    colour: C.success,
    endpoint: "/ask-db",
    mode: "json",
    needsCite: false,
    blurb: "What we already know about a site, a client or a job we've done before.",
    cta: "Look up a job",
    placeholder: "Site, client or job number...",
    greet: "Give me a site, a client or a job number and I'll tell you what we already know.",
    // 24 Sep 2026: the old chips were placeholder sites with nothing on file, so
    // the first thing a new user tapped answered "no stored facts". These three
    // are real and answer from the Hub. Swap them when these jobs go quiet.
    chips: ["Kahira Crescent", "JJ's Waste", "Job 32120"],
  },
  hs: {
    key: "hs",
    title: "Health & safety",
    scope: "Docs - templates - JSA",
    colour: C.danger,
    endpoint: "/health-safety",
    // NOT SSE. Unlike /regs, /howdoi and /processes, this one answers with a
    // plain JSON body { content:[{type:"text",text}], stop_reason }. Wiring it
    // as a stream meant the parser found no frames, produced empty text, and
    // the tool answered "I couldn't find this in the Hub." to every single
    // question (caught in smoke testing, 14 Sep 2026).
    mode: "jsonmsg",
    // needsCite is FALSE here on purpose. The brief asks for a clause on every
    // H&S answer, but /health-safety reads Bayside's own H&S documents out of
    // OneDrive and never returns a pdfRef, so enforcing a citation client-side
    // threw away every real answer and the tool replied "I couldn't find this
    // in the Hub" to everything (caught in smoke testing, 14 Sep 2026).
    // Turn this back on only once the worker actually supplies a reference.
    needsCite: false,
    blurb: "JSAs, toolbox talks, incident reporting, and the controls that go with them.",
    cta: "Do I need a JSA?",
    placeholder: "Ask about H&S...",
    greet: "What do you need, a JSA, a toolbox talk, or an incident write-up?",
    chips: ["Do I need a JSA?", "Toolbox talk template", "Report a near miss"],
  },
  processes: {
    key: "processes",
    title: "Bayside processes",
    scope: "How we do it here",
    colour: C.gold,
    endpoint: "/processes",
    mode: "sse",
    needsCite: false,
    blurb: "How we do things at Bayside. Paperwork, sign-off, who to tell.",
    cta: "Look up a process",
    placeholder: "Which process do you need?",
    greet: "Which process are you after? I'll pull it from our own guides.",
    chips: ["Booking a job in", "CoC and ESC", "Ordering materials"],
  },
};

const ORDER = ["regs", "tech", "job", "hs", "processes"];

// ============================================================
// DOCS - a card in the deck that is NOT a chat scope. It opens a
// page instead of a thread, so it deliberately lives OUTSIDE the
// TOOLS table: nothing in openTool, setMsgs, recents or the outbox
// must ever be handed a key that has no endpoint behind it.
// The deck renders TOOLS + DOCS; everything else still walks ORDER.
// ============================================================
const HANDBOOK = {
  key: "handbook",
  title: "Staff handbook",
  scope: "How we work here",
  colour: C.sea,
  // Canonical path, no ".html". Cloudflare Pages 308-redirects
  // /staff-handbook.html -> /staff-handbook, and a redirected response
  // cannot be written to the service worker cache, so the .html form
  // would silently never be available offline (caught 20 Sep 2026).
  href: "/staff-handbook",
  blurb: "Pay, leave, tools, vans, compliance and who to ring. The on-the-phone version.",
  cta: "Open the handbook",
};

// Same tab on purpose. The handbook page carries its own Back to the
// Hub button, and a new tab would drop staff out of the installed app.
function openDoc(doc) { try { window.location.href = doc.href; } catch (e) {} }

const DECK = ORDER.map(k => TOOLS[k]).concat([HANDBOOK]);

// H&S emergency contacts. 111 first, then the H&S lead.
const HS_LEAD = { label: "H&S lead", number: "022 039 9446", dial: "0220399446" };

// ============================================================
// LS - localStorage with the try/catch in one place. Private
// windows and cleared site data both throw, so every read has a
// fallback and every write is allowed to fail silently.
// ============================================================
const LS = {
  get(k, fb = null) { try { const v = localStorage.getItem(k); return v === null ? fb : v; } catch { return fb; } },
  getJSON(k, fb = null) { try { const v = localStorage.getItem(k); return v === null ? fb : JSON.parse(v); } catch { return fb; } },
  set(k, v) { try { localStorage.setItem(k, String(v)); return true; } catch { return false; } },
  setJSON(k, o) { try { localStorage.setItem(k, JSON.stringify(o)); return true; } catch { return false; } },
  remove(k) { try { localStorage.removeItem(k); } catch {} },
};

// ============================================================
// Photos (24 Sep 2026). Regulations and Tech support take a photo.
// Shrunk on the phone before it goes anywhere: 1568 px on the long
// side (the most the model uses) as a JPEG, so a 4 MB phone photo goes
// up as a few hundred KB over mobile data. A 320 px thumbnail is kept
// for the chat bubble. Only the thumbnail is ever written to local
// storage: a full photo there would fill it and the thread would stop
// saving without anyone knowing.
// ============================================================
function shrinkImage(file, maxSide, quality) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        const scale = Math.min(1, maxSide / Math.max(img.naturalWidth || 1, img.naturalHeight || 1));
        const w = Math.max(1, Math.round((img.naturalWidth || 1) * scale));
        const h = Math.max(1, Math.round((img.naturalHeight || 1) * scale));
        const c = document.createElement("canvas");
        c.width = w; c.height = h;
        c.getContext("2d").drawImage(img, 0, 0, w, h);
        URL.revokeObjectURL(url);
        resolve(c.toDataURL("image/jpeg", quality));
      } catch (e) { URL.revokeObjectURL(url); reject(e); }
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Couldn't read that photo")); };
    img.src = url;
  });
}
async function preparePhoto(file) {
  const full = await shrinkImage(file, 1568, 0.82);
  const thumb = await shrinkImage(file, 320, 0.6);
  return { data: full.split(",")[1] || "", thumb };
}
function stripPhotos(threads) {
  const out = {};
  for (const k of Object.keys(threads || {})) {
    const t = threads[k] || {};
    out[k] = Object.assign({}, t, { msgs: (t.msgs || []).map(m => m && m.image ? Object.assign({}, m, { image: { thumb: m.image.thumb || "" } }) : m) });
  }
  return out;
}

const uid = () =>
  (crypto && crypto.randomUUID) ? crypto.randomUUID()
  : "m" + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);

const token = () => LS.get(SESSION_TOKEN_KEY, "");

function authHeaders(extra) {
  // X-App-Version: saved with every question in the chat log (27 Sep 2026).
  const h = Object.assign({ "Content-Type": "application/json", "X-App-Version": APP_VERSION }, extra || {});
  const t = token();
  if (t) h["Authorization"] = "Bearer " + t;
  return h;
}

// Plain JSON call. Returns { ok, status, data }.
async function api(path, opts) {
  const o = opts || {};
  try {
    const res = await fetch(WORKER_URL + path, {
      method: o.method || "GET",
      headers: authHeaders(o.headers),
      body: o.body ? JSON.stringify(o.body) : undefined,
      signal: o.signal,
    });
    let data = null;
    try { data = await res.json(); } catch {}
    return { ok: res.ok, status: res.status, data: data || {} };
  } catch (e) {
    return { ok: false, status: 0, data: {}, error: e };
  }
}

// ============================================================
// askTool - the ONLY way a question reaches the backend.
//
// `toolKey` is required and is never inferred. Passing an unknown
// tool throws rather than falling back to a default, so a coding
// mistake surfaces in testing instead of quietly answering from
// the wrong scope (which is the bug this rebuild exists to fix).
//
// The worker's chat endpoints are SSE, not JSON. They take an
// Anthropic-style messages array (which is what carries the whole
// thread, so follow-ups keep their topic) and emit named events:
//   status -> { message }        progress, shown live
//   answer -> { content:[{type:"text",text}], pdfRef }
//   error  -> { message }
// /ask-db is the exception: plain JSON, { question, history }.
//
// Returns { text, cite, sources, found }.
// ============================================================
// ============================================================
// Answer rendering (15 Sep 2026).
// The models emit light markdown. Until now the chat printed it raw, so the
// crew read literal "**Toolbox Talk Template**" and a URL was dead text they
// had to retype into a browser. This turns the three things that actually
// show up into real elements, and nothing else: **bold**, a bare http(s) URL,
// and [label](url). Built as React nodes, never innerHTML, so a stray angle
// bracket in an answer can't become markup. Leading #, > and * bullet markers
// are stripped to a clean dash. Everything else is left exactly as written.
// ============================================================
const URL_RE = /(https?:\/\/[^\s<>()\[\]]+[^\s<>()\[\].,;:!?'"])/g;

function renderInline(text, C, kp) {
  const src = String(text || "")
    .replace(/^[ \t]*#{1,6}[ \t]+/gm, "")
    .replace(/^[ \t]*>[ \t]?/gm, "")
    .replace(/^[ \t]*[*+][ \t]+/gm, "- ");

  const out = [];
  let key = 0;
  const K = (p) => (kp || "") + p + key++;

  // [label](url) first, so its url isn't eaten by the bare-URL pass.
  const mdLink = /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g;
  let last = 0, m;
  const chunks = [];
  while ((m = mdLink.exec(src)) !== null) {
    if (m.index > last) chunks.push({ t: "text", v: src.slice(last, m.index) });
    chunks.push({ t: "link", v: m[1], href: m[2] });
    last = m.index + m[0].length;
  }
  if (last < src.length) chunks.push({ t: "text", v: src.slice(last) });

  const linkStyle = { color: C.orange, fontWeight: 600, textDecoration: "underline", wordBreak: "break-word" };

  const pushText = (s) => {
    // bare URLs
    const parts = s.split(URL_RE);
    parts.forEach((p, i) => {
      if (!p) return;
      if (i % 2 === 1) {
        out.push(React.createElement("a", {
          key: K("u"), href: p, target: "_blank", rel: "noopener noreferrer", style: linkStyle,
        }, p));
        return;
      }
      // **bold**
      const bits = p.split(/\*\*([^*\n]+)\*\*/g);
      bits.forEach((b, j) => {
        if (!b) return;
        out.push(j % 2 === 1
          ? React.createElement("strong", { key: K("b"), style: { fontWeight: 700 } }, b)
          : React.createElement(React.Fragment, { key: K("t") }, b));
      });
    });
  };

  chunks.forEach(c => {
    if (c.t === "link") {
      out.push(React.createElement("a", {
        key: K("l"), href: c.href, target: "_blank", rel: "noopener noreferrer", style: linkStyle,
      }, c.v));
    } else {
      pushText(c.v);
    }
  });

  return out;
}

// Markdown tables (24 Sep 2026). The regs tool is told to answer table
// questions with the table itself, and the chat printed it as raw pipes and
// "| --- | --- |", which is hard work on a phone. Rendered as a real table
// that scrolls sideways inside the bubble if it is wider than the screen.
const TABLE_ROW = /^\s*\|.*\|\s*$/;
const TABLE_SEP = /^\s*\|?\s*:?-{3,}/;
function renderTable(rows, C, k) {
  const cells = (l) => l.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map(c => c.trim());
  const head = cells(rows[0]);
  const body = rows.slice(1).map(cells);
  const th = { textAlign: "left", padding: "6px 10px", borderBottom: "2px solid " + C.navy, fontWeight: 700, color: C.navy, fontSize: 13, verticalAlign: "bottom" };
  const td = { padding: "6px 10px", borderBottom: "1px solid " + C.line, fontSize: 13.5, verticalAlign: "top" };
  return React.createElement("div", { key: k, className: "nb", style: { overflowX: "auto", margin: "6px 0", whiteSpace: "normal" } },
    React.createElement("table", { style: { borderCollapse: "collapse", minWidth: "100%" } },
      React.createElement("thead", null, React.createElement("tr", null,
        head.map((h, i) => React.createElement("th", { key: i, style: th }, renderInline(h, C, k + "h" + i + "_"))))),
      React.createElement("tbody", null,
        body.map((r, ri) => React.createElement("tr", { key: ri },
          r.map((c, ci) => React.createElement("td", { key: ci, style: td }, renderInline(c, C, k + "r" + ri + "c" + ci + "_"))))))));
}

function renderRich(text, C) {
  const lines = String(text || "").split("\n");
  const out = [];
  let buf = [];
  let seg = 0;
  const flush = () => {
    if (buf.length) { out.push(...renderInline(buf.join("\n"), C, "s" + seg++ + "_")); buf = []; }
  };
  for (let i = 0; i < lines.length; i++) {
    if (TABLE_ROW.test(lines[i]) && i + 1 < lines.length && TABLE_SEP.test(lines[i + 1])) {
      const rows = [lines[i]];
      let j = i + 2;
      while (j < lines.length && TABLE_ROW.test(lines[j])) { rows.push(lines[j]); j++; }
      flush();
      out.push(renderTable(rows, C, "tb" + seg++));
      i = j - 1;
      continue;
    }
    buf.push(lines[i]);
  }
  flush();
  return out;
}

// Plain text for the Copy button: the crew paste answers into Fergus notes and
// texts, where markdown asterisks are just noise.
function plainText(text) {
  return String(text || "")
    .replace(/\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g, "$1 ($2)")
    .replace(/\*\*([^*\n]+)\*\*/g, "$1")
    .replace(/^[ \t]*#{1,6}[ \t]+/gm, "")
    .replace(/^[ \t]*[*+][ \t]+/gm, "- ");
}

// The chip names the document properly and says PDF page, because the
// answer text quotes the PRINTED page number and the two differ by a few
// pages ("p.477" in the text, PDF page 481). Tapping it opens that page.
const DOC_NAMES = {
  esr2010: "Electricity (Safety) Regulations 2010",
  elecact1992: "Electricity Act 1992",
  hswa2015: "Health and Safety at Work Act 2015",
  hswgrwm2016: "HSW (General Risk) Regulations 2016",
  nzecp34: "NZECP 34",
  nzecp51: "NZECP 51",
};
function citeFromPdfRef(ref) {
  if (!ref || !ref.pdfKey) return null;
  const key = String(ref.pdfKey).trim();
  const name = DOC_NAMES[key] || (/^\d/.test(key) ? "AS/NZS " + key : key.replace(/[-_]/g, " "));
  return ref.pdfPage ? name + ", PDF page " + ref.pdfPage : name;
}

// Open the licensed PDF at the cited page (24 Sep 2026). The rebuild dropped
// the old View PDF button, so a citation could not be checked from the app.
// A single-use 60 second token scoped to that one file keeps the 30 day
// session token out of the address bar and browser history. The window is
// opened inside the tap itself so a phone's popup blocker lets it through.
async function openCite(ref, onToast) {
  if (!ref || !ref.pdfKey) return;
  let w = null;
  try { w = window.open("", "_blank"); } catch (e) { w = null; }
  try {
    const r = await api("/pdf-download-token", { method: "POST", body: { file: String(ref.pdfKey), page: ref.pdfPage || null } });
    if (!r.ok || !r.data || !r.data.token) throw new Error("no token");
    const url = WORKER_URL + "/regs-pdf?file=" + encodeURIComponent(String(ref.pdfKey)) +
      "&pdfToken=" + encodeURIComponent(r.data.token) + (ref.pdfPage ? "#page=" + ref.pdfPage : "");
    if (w) w.location.href = url; else window.location.href = url;
  } catch (e) {
    try { if (w) w.close(); } catch (e2) {}
    if (onToast) onToast("Couldn't open the document just now");
  }
}

// 4 Oct 2026: regs answers are reviewed by a second model. With onEarly the
// app asks for the answer straight away (reviewMode "early"); onEarly gets it
// marked reviewing, and the result returned at the end carries review:
// "ok", "corrected" (with the corrected answer) or "unchecked".
function applyAnswerPayload(out, payload) {
  // Joined with "", not a newline: a cited answer arrives as several text
  // blocks split mid-sentence at each citation.
  const text = (payload.content || [])
    .filter(b => b && b.type === "text")
    .map(b => b.text)
    .join("")
    .trim();
  out.text = text;
  out.cite = citeFromPdfRef(payload.pdfRef);
  out.ref = payload.pdfRef && payload.pdfRef.pdfKey ? payload.pdfRef : null;
  // A blank answer is a glitch, never "not in the Hub" (24 Sep 2026).
  out.blank = !!payload.blank || !text;
  // The worker sends found:false when it could not ground the answer
  // (NOT_IN_LIBRARY). Trust that over "did we get any text back", because
  // an ungrounded answer is still text. Older worker builds do not send
  // the field at all, so fall back to the old behaviour.
  out.found = (typeof payload.found === "boolean") ? payload.found : !!text;
  // On a miss the model sends a short "what would find it" hint. Keep it,
  // it is not an answer and it saves the next search.
  out.hint = out.found ? undefined : text;
  // 3 Oct 2026: the worker marks an answer webOnly when the standard asked
  // about is not in the library and the answer is web research it says so
  // up front. There is no licensed page to cite, so it must not be swapped
  // for "I couldn't find this in the Hub".
  out.webOnly = !!payload.webOnly;
}

async function askSSE(tool, history, onStatus, signal, onEarly) {
  // Photos: the two most recent photos still in memory go up as images, any
  // older one as a note that a photo was sent, to keep the request small.
  const withPhoto = history.map((m, i) => (m.image && m.image.data) ? i : -1).filter(i => i >= 0).slice(-2);
  const messages = history.map((m, i) => {
    const role = m.role === "assistant" ? "assistant" : "user";
    if (role === "user" && m.image && m.image.data && withPhoto.indexOf(i) >= 0) {
      return { role, content: [
        { type: "image", source: { type: "base64", media_type: "image/jpeg", data: m.image.data } },
        { type: "text", text: m.text || "Here's a photo. What does it show, and what matters here?" },
      ] };
    }
    if (role === "user" && m.image) return { role, content: (m.text ? m.text + " " : "") + "[photo sent earlier]" };
    return { role, content: m.text };
  });

  const res = await fetch(WORKER_URL + tool.endpoint, {
    method: "POST",
    headers: authHeaders({ Accept: "text/event-stream" }),
    body: JSON.stringify({ system: "", messages, site: "", reviewMode: onEarly ? "early" : undefined }),
    signal,
  });

  if (res.status === 401 || res.status === 403) throw new Error("SIGNED_OUT");
  if (!res.ok || !res.body) throw new Error("Server returned " + res.status);

  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  let out = { text: "", cite: null, sources: [], found: false };

  // SSE frames are separated by a blank line. Keep the tail in `buf`
  // because a frame can be split across two network chunks.
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const frames = buf.split("\n\n");
    buf = frames.pop() || "";

    for (const frame of frames) {
      let ev = "message";
      const dataLines = [];
      for (const line of frame.split("\n")) {
        if (line.startsWith(":")) continue;              // keepalive comment
        if (line.startsWith("event:")) ev = line.slice(6).trim();
        else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
      }
      if (!dataLines.length) continue;
      let payload = {};
      try { payload = JSON.parse(dataLines.join("\n")); } catch { continue; }

      if (ev === "status" && payload.message && onStatus) onStatus(payload.message);

      if (ev === "error") {
        // The answer is already on screen and only the review fell over:
        // keep the answer, mark it not reviewed.
        if (out.reviewing) { out.review = "unchecked"; continue; }
        throw new Error(payload.message || "Something went wrong");
      }

      if (ev === "answer") {
        applyAnswerPayload(out, payload);
        out.reviewing = !!payload.reviewing;
        if (out.reviewing && onEarly) onEarly(Object.assign({}, out));
      }

      if (ev === "review") {
        if (payload.corrected) { applyAnswerPayload(out, payload); out.review = "corrected"; }
        else out.review = payload.ok ? "ok" : "unchecked";
      }
    }
  }
  if (out.reviewing && !out.review) out.review = "unchecked";
  return out;
}

async function askJSON(tool, question, history, signal) {
  const res = await api(tool.endpoint, {
    method: "POST",
    signal,
    body: {
      question,
      history: history.slice(-6).map(m => ({ role: m.role, content: m.text })),
    },
  });
  if (res.status === 401 || res.status === 403) throw new Error("SIGNED_OUT");
  if (!res.ok) throw new Error(res.data.error || "Server returned " + res.status);

  const answer = (res.data.answer || "").trim();
  const rows = Array.isArray(res.data.rows) ? res.data.rows : [];
  return {
    text: answer,
    cite: null,
    // /ask-db rows are kb_facts, shaped { id, subject, client, site, kind, value,
    // jobno, source, updated }. They have no title/name/job, so the old mapping
    // fell all the way through to String(r.id) and rendered the DATABASE ROW ID
    // under every answer: "Schneider, 26226, 26225, 13939, 13935" is four row ids,
    // not four part numbers, and a sparky reading that on site is being handed
    // meaningless digits that look like catalogue numbers. Label from the real
    // fields, and drop the row entirely rather than ever showing a bare id.
    // (14 Sep 2026.)
    sources: rows.slice(0, 4).map(r => {
      const val = String(r.value || "").trim();
      if (!val) return { label: "", href: null };
      const where = String(r.subject || r.site || r.client || "").trim();
      const what = r.kind && r.kind !== "other" ? String(r.kind).trim() : "";
      const head = [where, what].filter(Boolean).join(" ");
      const job = String(r.jobno || "").trim();
      return {
        label: (head ? head + ": " : "") + val + (job ? " (job " + job + ")" : ""),
        href: r.url || r.href || null,
      };
    }).filter(s => s.label),
    found: !!answer,
  };
}

// Anthropic-shaped request, plain JSON reply. /health-safety only.
async function askJSONMessages(tool, history, signal) {
  const messages = history.map(m => ({
    role: m.role === "assistant" ? "assistant" : "user",
    content: m.text,
  }));
  const res = await api(tool.endpoint, { method: "POST", signal, body: { system: "", messages } });
  if (res.status === 401 || res.status === 403) throw new Error("SIGNED_OUT");
  if (!res.ok) throw new Error((res.data.error && res.data.error.message) || "Server returned " + res.status);
  const text = (res.data.content || [])
    .filter(b => b && b.type === "text")
    .map(b => b.text)
    .join("")
    .trim();
  return { text, cite: null, sources: [], found: !!text, blank: !text };
}

async function askTool(toolKey, question, history, onStatus, signal, onEarly) {
  const tool = TOOLS[toolKey];
  if (!tool) throw new Error("askTool called without a valid tool: " + toolKey);

  const result = tool.mode === "json"
    ? await askJSON(tool, question, history, signal)
    : tool.mode === "jsonmsg"
    ? await askJSONMessages(tool, history, signal)
    : await askSSE(tool, history, onStatus, signal, onEarly);

  // A regs or H&S answer with no clause behind it is not an answer we
  // will stand behind, so it is reported as not found rather than
  // shown bare. Until the worker returns an explicit `found` and
  // `cite`, this is the only accuracy guard the app can enforce.
  if (tool.needsCite && result.found && !result.cite && !result.webOnly) result.uncited = true;
  return result;
}

const NOT_FOUND_TEXT = "I couldn't find this in the Hub.";
const BLANK_TEXT = "That one came back blank, which is a glitch on our end, not a gap in what we know. Send it again.";

// One place that turns a tool result into the assistant message, so a live
// send and an outbox flush can never drift apart again.
function answerMessage(r) {
  if (r.blank) return { id: uid(), role: "assistant", text: BLANK_TEXT, cite: null, ref: null, sources: [], acts: false, isError: true };
  const ok = r.found && !r.uncited;
  return {
    id: uid(), role: "assistant",
    text: ok ? r.text : (r.hint ? NOT_FOUND_TEXT + "\n\n" + r.hint : NOT_FOUND_TEXT),
    cite: ok ? r.cite : null, ref: ok ? (r.ref || null) : null,
    sources: ok ? (r.sources || []) : [], acts: ok, miss: !ok,
    review: r.reviewing ? (r.review || "unchecked") : undefined,
  };
}

// ============================================================
// Outbox - queued questions survive a force-quit. Each carries its
// tool and a clientMsgId so a retry cannot duplicate an answer.
// ============================================================
const Outbox = {
  all() { const v = LS.getJSON(OUTBOX_KEY, []); return Array.isArray(v) ? v : []; },
  add(item) { const q = Outbox.all(); q.push(item); LS.setJSON(OUTBOX_KEY, q); return q; },
  remove(id) { const q = Outbox.all().filter(i => i.clientMsgId !== id); LS.setJSON(OUTBOX_KEY, q); return q; },
  clear() { LS.remove(OUTBOX_KEY); },
};

// ============================================================
// Icons - Lucide paths, 2px stroke, currentColor. No emoji.
// ============================================================
const PATHS = {
  regs: ["M12 7v14", "M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z"],
  tech: ["M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"],
  job: ["M3 5v14a9 3 0 0 0 18 0V5", "M3 12a9 3 0 0 0 18 0"],
  hs: ["M2 18a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1z", "M10 10V5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v5", "M4 15v-3a8 8 0 0 1 16 0v3"],
  processes: ["M9 11V6a3 3 0 0 1 6 0v5", "M5 11h14l-1 10H6z"],
  sweep: ["M9 11l3 3L22 4", "M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"],
  back: ["m15 18-6-6 6-6"],
  send: ["M12 19V5", "m5 12 7-7 7 7"],
  copy: ["M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"],
  up: ["M7 10v12", "M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88"],
  down: ["M17 14V2", "M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88"],
  bug: ["M12 20v-9", "M6.38 8.5 4 7", "M18 7l-2.38 1.5", "M8 4a4 4 0 0 1 8 0", "M6 11a6 6 0 0 0 12 0v-1a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2z"],
  chev: ["m9 18 6-6-6-6"],
  newchat: ["M12 8v6", "M9 11h6", "M21 15a2 2 0 0 1-2 2H8l-4 4V5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z"],
  camera: ["M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"],
  x: ["M18 6 6 18", "m6 6 12 12"],
  handbook: ["M4 19.5A2.5 2.5 0 0 1 6.5 17H20", "M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z", "M9 7h7"],
};

function Icon({ name, size = 22, colour = "currentColor", width = 1.8 }) {
  const d = PATHS[name] || [];
  const kids = d.map((p, i) => React.createElement("path", { key: i, d: p }));
  if (name === "job") kids.unshift(React.createElement("ellipse", { key: "e", cx: 12, cy: 5, rx: 9, ry: 3 }));
  if (name === "camera") kids.push(React.createElement("circle", { key: "c", cx: 12, cy: 13, r: 3 }));
  if (name === "copy") kids.unshift(React.createElement("rect", { key: "r", x: 9, y: 9, width: 13, height: 13, rx: 3 }));
  return React.createElement("svg", {
    width: size, height: size, viewBox: "0 0 24 24", fill: "none",
    stroke: colour, strokeWidth: width, strokeLinecap: "round", strokeLinejoin: "round",
    "aria-hidden": "true",
  }, kids);
}

// ============================================================
// LOGIN - name plus PIN against POST /login. The big circle is the
// affordance from the design; it opens the keypad. It is NOT a
// biometric yet: real Face ID needs WebAuthn registration and a
// server-side credential store, which is a separate piece of work.
// Calling a PIN pad "Face ID" would be a lie to the user, so the
// label says what it does.
// ============================================================
function Login({ onSignedIn }) {
  const [name, setName] = React.useState(LS.get(LAST_NAME_KEY, ""));
  const [pin, setPin] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [shake, setShake] = React.useState(false);
  const pinRef = React.useRef(null);

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (busy) return;
    const n = name.trim();
    if (!n || pin.length < 4) { setErr("Enter your name and PIN."); return; }
    setBusy(true); setErr("");
    const res = await api("/login", { method: "POST", body: { name: n, pin, rememberMe: true } });
    setBusy(false);
    if (res.ok && res.data && res.data.token) {
      LS.set(SESSION_TOKEN_KEY, res.data.token);
      LS.set(LAST_NAME_KEY, n);
      onSignedIn(res.data);
      return;
    }
    setPin("");
    setShake(true); setTimeout(() => setShake(false), 420);
    setErr((res.data && res.data.error) || "That didn't work. Check your name and PIN.");
    if (pinRef.current) pinRef.current.focus();
  };

  const field = {
    width: "100%", boxSizing: "border-box", border: "none", borderRadius: 12,
    padding: "16px 18px", fontSize: 17, fontFamily: "Inter,system-ui,sans-serif",
    background: "rgba(255,255,255,.08)", color: "#fff", outline: "none",
  };

  return React.createElement("form", {
    onSubmit: submit,
    style: {
      height: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", gap: 40, padding: "48px 28px",
      transform: shake ? "translateX(0)" : "none",
      animation: shake ? "bayshake .4s" : "none",
    },
  },
    React.createElement("div", { style: { display: "flex", flexDirection: "column", alignItems: "center", gap: 16 } },
      React.createElement("div", { style: { width: 84, height: 84, borderRadius: 26, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center" } },
        React.createElement("img", { src: "bayside-symbol.png", alt: "Bayside", style: { width: 54, height: "auto" } })),
      React.createElement("div", { style: { fontFamily: "Barlow,sans-serif", fontWeight: 800, fontSize: 32, letterSpacing: "-.02em", color: "#fff" } }, "Bayside Hub"),
      React.createElement("div", { style: { fontSize: 11, fontWeight: 600, letterSpacing: ".16em", textTransform: "uppercase", color: "rgba(255,255,255,.38)" } }, "Reliable - Efficient")
    ),

    React.createElement("div", { style: { display: "flex", flexDirection: "column", gap: 14, width: "100%", maxWidth: 320, boxSizing: "border-box" } },
      React.createElement("input", {
        className: "bay-login-input",
        value: name, onChange: e => setName(e.target.value), placeholder: "Your name",
        autoComplete: "username", autoCapitalize: "words", style: field, "aria-label": "Your name",
      }),
      React.createElement("input", {
        className: "bay-login-input",
        ref: pinRef, value: pin, onChange: e => setPin(e.target.value.replace(/\D/g, "").slice(0, 8)),
        placeholder: "PIN", inputMode: "numeric", type: "password", autoComplete: "current-password",
        style: Object.assign({}, field, { letterSpacing: ".3em" }), "aria-label": "PIN",
      }),
      err ? React.createElement("div", { role: "alert", style: { fontSize: 13.5, fontWeight: 600, color: "#FFB4A6", textAlign: "center" } }, err) : null,
      React.createElement("button", {
        type: "submit", disabled: busy,
        style: {
          minHeight: 52, border: "none", borderRadius: 999, background: busy ? "#B8431A" : C.orange,
          color: "#fff", fontFamily: "Barlow,sans-serif", fontWeight: 700, fontSize: 17,
          cursor: busy ? "default" : "pointer", transition: "background .15s " + EASE,
        },
      }, busy ? "Signing in..." : "Sign in")
    ),

    React.createElement("div", { style: { fontSize: 11, color: "rgba(255,255,255,.24)" } },
      "Bayside Electrical Services Ltd - v" + APP_VERSION)
  );
}

// ============================================================
// SweepCard - apprentice On-Job sweep. The Hub only QUEUES the
// request; a scheduled task on the always-on server polls
// /apprentice-sweep-pending, does the marking and posts back to
// /apprentice-sweep-done. The Hub cannot reach the folders itself.
// Admin (Ryan) sees a button per apprentice and skips the 24h
// cooldown; an apprentice sees only their own.
// ============================================================
function SweepCard({ onToast }) {
  const [state, setState] = React.useState(null);
  const [busy, setBusy] = React.useState("");
  const [editTotals, setEditTotals] = React.useState(null);   // { apprentice: "29" } while Ryan edits

  const load = React.useCallback(async () => {
    const res = await api("/apprentice-sweep-status");
    if (res.ok && res.data && res.data.ok) setState(res.data);
    else setState({ mode: "none" });
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const run = async (target) => {
    setBusy(target || "self");
    const res = await api("/apprentice-sweep", { method: "POST", body: target ? { target } : {} });
    setBusy("");
    if (res.ok && res.data && res.data.ok) {
      onToast("Sweep queued for " + (res.data.apprentice || "you"));
      load();
    } else {
      onToast((res.data && res.data.error) || "Couldn't queue the sweep");
    }
  };

  if (!state || state.mode === "none") return null;

  const rows = state.mode === "admin"
    ? (state.list || [])
    : [{ apprentice: state.apprentice, canRun: state.canRun, lastDone: state.lastDone, status: state.status, stuck: state.stuck, stuckSince: state.stuckSince, summary: state.summary, total: state.total }];

  // Status at a glance (27 Sep 2026, Ryan). Counts per apprentice from the
  // marking folders: signed off, passed and waiting for Ryan's signature,
  // Not Yet (needs a resubmission), and in progress (in the Drop folder with
  // no result yet). Unit numbers sit under each count except signed, which
  // gets long. No table if the worker could not read OneDrive.
  const SUM_COLS = [
    { k: "signed", label: "Signed off", nums: false },
    { k: "toSign", label: "To sign", nums: true },
    { k: "notYet", label: "Not yet", nums: true },
    { k: "inProgress", label: "In progress", nums: true },
  ];
  const withSum = rows.filter(r => r.summary);
  const cell = { padding: "7px 4px", textAlign: "center", borderTop: "1px solid " + C.line, verticalAlign: "top" };
  const sumTable = withSum.length ? React.createElement("table", {
    style: { width: "100%", borderCollapse: "collapse", fontFamily: "Inter,sans-serif", tableLayout: "fixed" },
  },
    React.createElement("thead", null, React.createElement("tr", null,
      state.mode === "admin" ? React.createElement("th", { style: { width: "22%" } }) : null,
      SUM_COLS.map(c => React.createElement("th", { key: c.k, style: { fontSize: 11, fontWeight: 600, color: C.mid, padding: "0 4px 6px", textAlign: "center" } }, c.label))
    )),
    React.createElement("tbody", null, withSum.map(r => React.createElement("tr", { key: r.apprentice },
      state.mode === "admin" ? React.createElement("td", { style: Object.assign({}, cell, { textAlign: "left", fontSize: 13, fontWeight: 600, color: C.char }) }, String(r.apprentice).split(/\s+/)[0]) : null,
      SUM_COLS.map(c => {
        const list = (r.summary[c.k] || []);
        const hot = list.length && (c.k === "toSign" || c.k === "notYet");
        return React.createElement("td", { key: c.k, style: cell },
          React.createElement("div", { style: { fontFamily: "Barlow,sans-serif", fontWeight: 800, fontSize: 19, color: hot ? (c.k === "toSign" ? C.orange : C.danger) : (list.length ? C.navy : C.mid) } },
            String(list.length),
            (c.k === "signed" && r.total) ? React.createElement("span", { style: { fontFamily: "Inter,sans-serif", fontWeight: 600, fontSize: 12, color: C.mid } }, " of " + r.total) : null),
          (c.nums && list.length) ? React.createElement("div", { style: { fontSize: 11, color: C.mid, marginTop: 1, lineHeight: 1.3 } }, "OJ " + list.join(", ")) : null
        );
      })
    )))
  ) : null;

  const when = (iso) => {
    if (!iso) return "not run yet";
    const d = new Date(iso);
    if (isNaN(d)) return "not run yet";
    return "last run " + d.toLocaleDateString("en-NZ", { day: "numeric", month: "short" });
  };

  return React.createElement("div", {
    style: { background: C.off, borderRadius: 22, padding: 24, boxSizing: "border-box", display: "flex", flexDirection: "column", gap: 16 },
  },
    React.createElement(Icon, { name: "sweep", size: 26, colour: C.navy, width: 1.7 }),
    React.createElement("div", null,
      React.createElement("div", { style: { fontFamily: "Barlow,sans-serif", fontWeight: 800, fontSize: 21, color: C.navy, letterSpacing: "-.02em" } }, "On-Job sweep"),
      React.createElement("div", { style: { fontSize: 13.5, color: C.body, lineHeight: 1.5, marginTop: 5 } },
        "Load your assessment into your Drop folder, then run this and it gets marked.")
    ),
    sumTable,
    // Ryan sets each apprentice's all-up On-Job count once, so Signed off
    // reads "13 of 29". Nothing in the marking folders records the total.
    (state.mode === "admin" && withSum.length) ? (editTotals
      ? React.createElement("div", { style: { display: "flex", flexDirection: "column", gap: 8 } },
          withSum.map(r => React.createElement("label", { key: r.apprentice, style: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, fontSize: 13, color: C.char } },
            String(r.apprentice).split(/\s+/)[0] + ", On-Jobs all up",
            React.createElement("input", {
              type: "number", inputMode: "numeric", min: 0, max: 200,
              value: editTotals[r.apprentice] || "",
              onChange: e => { const v = e.target.value; setEditTotals(prev => Object.assign({}, prev, { [r.apprentice]: v })); },
              style: { width: 76, minHeight: 40, boxSizing: "border-box", border: "1px solid " + C.line, borderRadius: 10, padding: "0 10px", fontSize: 16, fontFamily: "Inter,sans-serif", color: C.char, textAlign: "center" },
            }))),
          React.createElement("div", { style: { display: "flex", gap: 10 } },
            React.createElement("button", {
              onClick: () => setEditTotals(null),
              style: { flex: 1, minHeight: 44, borderRadius: 999, border: "1px solid " + C.line, background: "#fff", color: C.char, fontFamily: "Barlow,sans-serif", fontWeight: 700, fontSize: 14, cursor: "pointer" },
            }, "Cancel"),
            React.createElement("button", {
              onClick: async () => {
                let ok = true;
                for (const r of withSum) {
                  const v = parseInt(editTotals[r.apprentice], 10);
                  const want = isNaN(v) ? 0 : v;
                  if (want === (r.total || 0)) continue;
                  const res = await api("/apprentice-sweep-total", { method: "POST", body: { apprentice: r.apprentice, total: want } });
                  if (!(res.ok && res.data && res.data.ok)) ok = false;
                }
                setEditTotals(null);
                onToast(ok ? "Totals saved" : "Couldn't save a total, try again");
                load();
              },
              style: { flex: 2, minHeight: 44, borderRadius: 999, border: "none", background: C.navy, color: "#fff", fontFamily: "Barlow,sans-serif", fontWeight: 700, fontSize: 14, cursor: "pointer" },
            }, "Save totals")))
      : React.createElement("button", {
          onClick: () => { const m = {}; withSum.forEach(r => { m[r.apprentice] = r.total ? String(r.total) : ""; }); setEditTotals(m); },
          style: { alignSelf: "flex-start", background: "transparent", border: "none", padding: 0, minHeight: 36, cursor: "pointer", fontFamily: "Inter,sans-serif", fontSize: 12, fontWeight: 600, color: C.mid, textDecoration: "underline" },
        }, withSum.some(r => !r.total) ? "Set On-Job totals" : "Edit On-Job totals")) : null,
    React.createElement("div", { style: { display: "flex", flexDirection: "column", gap: 10, paddingTop: 4 } },
      rows.map(r => React.createElement("div", {
        key: r.apprentice,
        style: { display: "flex", alignItems: "center", gap: 12, justifyContent: "space-between" },
      },
        React.createElement("div", { style: { minWidth: 0 } },
          React.createElement("div", { style: { fontSize: 14, fontWeight: 600, color: C.char } }, r.apprentice),
          // Never say "marking shortly" about a sweep that is not running. The
          // worker flags anything pending over 2 hours as stuck, because the
          // marking happens on a machine the Hub cannot see and it was dead for
          // four weeks without anyone being told. (15 Sep 2026)
          React.createElement("div", {
            style: { fontSize: 11.5, color: r.stuck ? C.danger : C.mid, fontWeight: r.stuck ? 600 : 500, marginTop: 2 },
          },
            r.stuck
              ? "Marking didn't run. Ryan has been told, tap to try again."
              : (r.status === "pending" ? "Queued, marking shortly" : when(r.lastDone)))
        ),
        React.createElement("button", {
          onClick: () => run(state.mode === "admin" ? r.apprentice : null),
          // A stuck sweep re-enables the button. Previously "pending" disabled
          // it forever, so a poller that stopped answering bricked the feature
          // for that apprentice with no way back.
          disabled: !r.canRun || busy === r.apprentice || (r.status === "pending" && !r.stuck),
          style: {
            minHeight: 44, padding: "0 18px", borderRadius: 999, border: "none", flexShrink: 0,
            background: (!r.canRun || (r.status === "pending" && !r.stuck)) ? C.line : (r.stuck ? C.danger : C.navy),
            color: (!r.canRun || (r.status === "pending" && !r.stuck)) ? C.mid : "#fff",
            fontFamily: "Barlow,sans-serif", fontWeight: 700, fontSize: 14,
            cursor: (!r.canRun || (r.status === "pending" && !r.stuck)) ? "default" : "pointer",
            transition: "background .15s " + EASE,
          },
        }, busy === r.apprentice ? "Queueing..." : (r.stuck ? "Try again" : (r.status === "pending" ? "Queued" : (r.canRun ? "Run sweep" : "Done today"))))
      ))
    )
  );
}

// ============================================================
// ADMIN - staff registry. Ryan only.
// The worker has had the whole backend since Session B and it is solid:
//   GET  /registry  admin-only, returns { approved, pending } with name, role,
//                   failedAttempts, lockedUntil, hasPin. PIN material is
//                   stripped server-side and never reaches the browser.
//   POST /registry  admin-only, merges by name under an eTag so two admins
//                   cannot silently overwrite each other. Setting a plain
//                   `pin` (4 to 6 digits) on an entry resets that person and
//                   clears their lockout; leaving it off carries their
//                   existing credentials forward untouched.
// The v1 rebuild simply dropped the UI, so there was no way to approve a
// request, reset a PIN or add a login. Restored 15 Sep 2026.
// ============================================================
function AdminPanel({ onToast }) {
  const [reg, setReg] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [resetting, setResetting] = React.useState(null);   // name being PIN-reset
  const [pinDraft, setPinDraft] = React.useState("");
  const [nName, setNName] = React.useState("");
  const [nPin, setNPin] = React.useState("");
  const [nRole, setNRole] = React.useState("staff");

  const load = React.useCallback(async () => {
    const res = await api("/registry");
    if (res.status === 403) { onToast("Admin only"); setReg({ approved: [], pending: [] }); return; }
    const d = res.data || {};
    setReg({ approved: Array.isArray(d.approved) ? d.approved : [], pending: Array.isArray(d.pending) ? d.pending : [] });
  }, [onToast]);

  React.useEffect(() => { load(); }, [load]);

  // Always send the WHOLE registry back. The worker merges by name, so any
  // entry we pass through unchanged keeps its PIN.
  const save = async (next, msg) => {
    setBusy(true);
    const res = await api("/registry", { method: "POST", body: next });
    setBusy(false);
    if (res.status === 409) { onToast("Someone else just changed it, reloading"); load(); return; }
    if (!res.ok && res.status !== 200) { onToast((res.data && res.data.error) || "Couldn't save"); return; }
    onToast(msg || "Saved");
    setReg(next);
    load();
  };

  if (!reg) return React.createElement("div", { style: { padding: 30, color: C.mid, fontSize: 14 } }, "Loading team...");

  const approve = (p) => save({
    approved: reg.approved.concat([{ name: p.name, role: "staff", failedAttempts: 0, lockedUntil: 0 }]),
    pending: reg.pending.filter(x => x.name !== p.name),
  }, p.name + " can now sign in");

  const decline = (p) => save({
    approved: reg.approved,
    pending: reg.pending.filter(x => x.name !== p.name),
  }, "Request declined");

  const setRole = (a) => save({
    approved: reg.approved.map(x => x.name === a.name ? Object.assign({}, x, { role: x.role === "admin" ? "staff" : "admin" }) : x),
    pending: reg.pending,
  }, a.name + " is now " + (a.role === "admin" ? "staff" : "an admin"));

  const unlock = (a) => save({
    approved: reg.approved.map(x => x.name === a.name ? Object.assign({}, x, { failedAttempts: 0, lockedUntil: 0 }) : x),
    pending: reg.pending,
  }, a.name + " unlocked");

  const remove = (a) => save({
    approved: reg.approved.filter(x => x.name !== a.name),
    pending: reg.pending,
  }, a.name + " removed");

  const doReset = () => {
    if (!/^\d{4,6}$/.test(pinDraft)) { onToast("PIN must be 4 to 6 digits"); return; }
    const nm = resetting;
    save({
      approved: reg.approved.map(x => x.name === nm ? Object.assign({}, x, { pin: pinDraft }) : x),
      pending: reg.pending,
    }, "New PIN set for " + nm);
    setResetting(null); setPinDraft("");
  };

  const addPerson = () => {
    const nm = nName.trim();
    if (!nm) { onToast("Give them a name"); return; }
    if (!/^\d{4,6}$/.test(nPin)) { onToast("PIN must be 4 to 6 digits"); return; }
    if ([...reg.approved, ...reg.pending].some(x => (x.name || "").trim().toLowerCase() === nm.toLowerCase())) {
      onToast("That name is already registered"); return;
    }
    save({
      approved: reg.approved.concat([{ name: nm, role: nRole, pin: nPin, failedAttempts: 0, lockedUntil: 0 }]),
      pending: reg.pending,
    }, nm + " added, they can sign in now");
    setNName(""); setNPin(""); setNRole("staff");
  };

  const card = { background: C.off, borderRadius: 22, padding: 24, display: "flex", flexDirection: "column", gap: 16 };
  const h = { fontFamily: "Barlow,sans-serif", fontWeight: 800, fontSize: 21, color: C.navy, letterSpacing: "-.02em" };
  const btn = (bg, fg) => ({
    minHeight: 40, padding: "0 14px", borderRadius: 999, border: "none", flexShrink: 0,
    background: bg, color: fg || "#fff", fontFamily: "Barlow,sans-serif", fontWeight: 700,
    fontSize: 13, cursor: busy ? "default" : "pointer", transition: "background .15s " + EASE,
  });
  const inp = {
    minHeight: 44, padding: "0 14px", borderRadius: 12, border: "1px solid " + C.line,
    fontSize: 15, fontFamily: "Inter,sans-serif", background: "#fff", color: C.char, minWidth: 0,
  };
  const row = { display: "flex", alignItems: "center", gap: 10, justifyContent: "space-between", flexWrap: "wrap" };

  const lockedNow = (a) => a.lockedUntil && a.lockedUntil > Date.now();

  // Sign in once: ask the worker for a 60 second, single-use code and pass it
  // in the URL fragment (never sent to a server, and the registry strips it
  // from the address bar on arrival). The tab is opened BEFORE the await so
  // the popup blocker sees a direct tap. Any failure just opens the registry's
  // normal sign-in screen. (5 Oct 2026)
  const openRegistry = async () => {
    const REG = "https://bayside-registry.pages.dev/";
    let w = null;
    try { w = window.open("", "_blank"); } catch (e) { w = null; }
    let url = REG;
    try {
      const r = await api("/handoff-code", { method: "POST", body: { aud: "registry" } });
      if (r.ok && r.data && r.data.code) url = REG + "#h=" + encodeURIComponent(r.data.code);
    } catch (e) { /* fall back to the plain sign-in screen */ }
    if (w && !w.closed) { try { w.opener = null; } catch (e) {} w.location.href = url; }
    else window.location.href = url;
  };

  return React.createElement("div", { style: { display: "flex", flexDirection: "column", gap: 18, padding: "4px 0" } },

    // ── Bayside Registry (vehicles, people, equipment, renewals). Separate
    //    Pages site, same name and PIN as the Hub. It has its own allow list
    //    (ALLOWED_USERS in its wrangler.toml). Added 5 Oct 2026.
    React.createElement("div", { style: Object.assign({}, card, { flexDirection: "row", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }) },
      React.createElement("div", { style: { minWidth: 0, flex: "1 1 240px" } },
        React.createElement("div", { style: h }, "Bayside Registry"),
        React.createElement("div", { style: { fontSize: 13.5, color: C.body, lineHeight: 1.5, marginTop: 5 } },
          "Vehicles, people, training, equipment, insurance and renewals. Sign in with your Hub name and PIN.")),
      React.createElement("button", {
        onClick: openRegistry,
        style: Object.assign({}, btn(C.navy), { minHeight: 44, cursor: "pointer" }),
      }, "Open the registry")
    ),

    // ── Waiting to be approved ───────────────────────────────────────────
    React.createElement("div", { style: card },
      React.createElement("div", null,
        React.createElement("div", { style: h }, "Waiting for approval" + (reg.pending.length ? " (" + reg.pending.length + ")" : "")),
        React.createElement("div", { style: { fontSize: 13.5, color: C.body, lineHeight: 1.5, marginTop: 5 } },
          "Anyone who asked for access from the sign-in screen. They set their own PIN, you just say yes or no.")),
      reg.pending.length === 0
        ? React.createElement("div", { style: { fontSize: 13.5, color: C.mid } }, "Nobody waiting.")
        : React.createElement("div", { style: { display: "flex", flexDirection: "column", gap: 12 } },
            reg.pending.map(p => React.createElement("div", { key: p.name, style: row },
              React.createElement("div", { style: { minWidth: 0 } },
                React.createElement("div", { style: { fontSize: 14.5, fontWeight: 600, color: C.char } }, p.name),
                React.createElement("div", { style: { fontSize: 11.5, color: C.mid, marginTop: 2 } },
                  p.hasPin ? "PIN set, ready to approve" : "No PIN set yet")),
              React.createElement("div", { style: { display: "flex", gap: 8 } },
                React.createElement("button", { onClick: () => approve(p), disabled: busy, style: btn(C.navy) }, "Approve"),
                React.createElement("button", { onClick: () => decline(p), disabled: busy, style: btn("transparent", C.danger) }, "Decline"))
            )))
    ),

    // ── The team ─────────────────────────────────────────────────────────
    React.createElement("div", { style: card },
      React.createElement("div", { style: h }, "Team logins (" + reg.approved.length + ")"),
      React.createElement("div", { style: { display: "flex", flexDirection: "column", gap: 14 } },
        reg.approved.map(a => React.createElement("div", { key: a.name, style: { display: "flex", flexDirection: "column", gap: 8 } },
          React.createElement("div", { style: row },
            React.createElement("div", { style: { minWidth: 0 } },
              React.createElement("div", { style: { fontSize: 14.5, fontWeight: 600, color: C.char } },
                a.name,
                a.role === "admin" ? React.createElement("span", {
                  style: { marginLeft: 8, fontSize: 10, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: C.orange },
                }, "Admin") : null),
              React.createElement("div", { style: { fontSize: 11.5, color: lockedNow(a) ? C.danger : C.mid, marginTop: 2, fontWeight: lockedNow(a) ? 600 : 500 } },
                lockedNow(a)
                  ? "Locked out until " + new Date(a.lockedUntil).toLocaleTimeString("en-NZ", { hour: "numeric", minute: "2-digit" })
                  : (a.hasPin ? "PIN set" : "No PIN, they cannot sign in"))),
            React.createElement("div", { style: { display: "flex", gap: 8, flexWrap: "wrap" } },
              React.createElement("button", { onClick: () => setResetting(resetting === a.name ? null : a.name), disabled: busy, style: btn(C.line, C.char) }, "Reset PIN"),
              lockedNow(a) ? React.createElement("button", { onClick: () => unlock(a), disabled: busy, style: btn(C.danger) }, "Unlock") : null,
              React.createElement("button", { onClick: () => setRole(a), disabled: busy, style: btn(C.line, C.char) }, a.role === "admin" ? "Make staff" : "Make admin"),
              React.createElement("button", { onClick: () => remove(a), disabled: busy, style: btn("transparent", C.danger) }, "Remove"))
          ),
          resetting === a.name ? React.createElement("div", { style: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" } },
            React.createElement("input", {
              value: pinDraft, onChange: e => setPinDraft(e.target.value.replace(/\D/g, "").slice(0, 6)),
              placeholder: "New 4 to 6 digit PIN", inputMode: "numeric", autoFocus: true,
              style: Object.assign({}, inp, { flex: "1 1 200px" }),
            }),
            React.createElement("button", { onClick: doReset, disabled: busy, style: btn(C.navy) }, "Set PIN")
          ) : null
        )))
    ),

    // ── Add someone ──────────────────────────────────────────────────────
    React.createElement("div", { style: card },
      React.createElement("div", null,
        React.createElement("div", { style: h }, "Add a login"),
        React.createElement("div", { style: { fontSize: 13.5, color: C.body, lineHeight: 1.5, marginTop: 5 } },
          "Creates the login straight away, no approval step. Use it for a new starter, or for a test login when you want to see what the crew see.")),
      React.createElement("div", { style: { display: "flex", gap: 10, flexWrap: "wrap" } },
        React.createElement("input", {
          value: nName, onChange: e => setNName(e.target.value), placeholder: "Full name",
          style: Object.assign({}, inp, { flex: "2 1 220px" }),
        }),
        React.createElement("input", {
          value: nPin, onChange: e => setNPin(e.target.value.replace(/\D/g, "").slice(0, 6)),
          placeholder: "PIN", inputMode: "numeric",
          style: Object.assign({}, inp, { flex: "1 1 110px" }),
        }),
        React.createElement("button", {
          onClick: () => setNRole(nRole === "admin" ? "staff" : "admin"), disabled: busy,
          style: Object.assign({}, btn(nRole === "admin" ? C.orange : C.line, nRole === "admin" ? "#fff" : C.char), { minHeight: 44 }),
        }, nRole === "admin" ? "Admin" : "Staff"),
        React.createElement("button", { onClick: addPerson, disabled: busy, style: Object.assign({}, btn(C.navy), { minHeight: 44 }) }, "Add")
      )
    )
  );
}

// ============================================================
// HOME
// ============================================================
function ToolCard({ tool, onOpen, wide }) {
  return React.createElement("div", {
    onClick: onOpen,
    role: "button",
    tabIndex: 0,
    onKeyDown: e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } },
    style: {
      flex: wide ? "1 1 auto" : "0 0 86%",
      scrollSnapAlign: "start",
      background: C.off, borderRadius: 22, padding: 24, boxSizing: "border-box",
      display: "flex", flexDirection: "column", gap: 18, cursor: "pointer",
      minHeight: 224, transition: "background .2s " + EASE,
    },
  },
    React.createElement(Icon, { name: tool.key, size: 26, colour: tool.colour, width: 1.7 }),
    React.createElement("div", null,
      React.createElement("div", { style: { fontFamily: "Barlow,sans-serif", fontWeight: 800, fontSize: 21, color: C.navy, letterSpacing: "-.02em" } }, tool.title),
      React.createElement("div", { style: { fontSize: 13.5, color: C.body, lineHeight: 1.5, marginTop: 5 } }, tool.blurb)
    ),
    tool.key === "hs" ? React.createElement("div", {
      style: { display: "flex", gap: 22, paddingTop: 14, borderTop: "1px solid " + C.line },
      onClick: e => e.stopPropagation(),
    },
      React.createElement("a", { href: "tel:111", style: { textDecoration: "none", display: "flex", flexDirection: "column", gap: 1 } },
        React.createElement("span", { style: { fontSize: 9.5, fontWeight: 700, letterSpacing: ".09em", textTransform: "uppercase", color: C.mid } }, "Emergency"),
        React.createElement("span", { style: { fontFamily: "Barlow,sans-serif", fontSize: 17, fontWeight: 800, color: C.danger } }, "111")),
      React.createElement("a", { href: "tel:" + HS_LEAD.dial, style: { textDecoration: "none", display: "flex", flexDirection: "column", gap: 1 } },
        React.createElement("span", { style: { fontSize: 9.5, fontWeight: 700, letterSpacing: ".09em", textTransform: "uppercase", color: C.mid } }, HS_LEAD.label),
        React.createElement("span", { style: { fontFamily: "Barlow,sans-serif", fontSize: 17, fontWeight: 800, color: C.danger } }, HS_LEAD.number))
    ) : null,
    React.createElement("div", { style: { marginTop: "auto", display: "flex", alignItems: "center", gap: 8 } },
      React.createElement("span", { style: { fontFamily: "Barlow,sans-serif", fontWeight: 700, fontSize: 14.5, color: C.orange } }, tool.cta),
      React.createElement(Icon, { name: "chev", size: 16, colour: C.orange, width: 2.2 })
    )
  );
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Morning";
  if (h < 17) return "Afternoon";
  return "Evening";
}

function Home({ user, wide, threads, onOpen, onToast, onBug }) {
  const deckRef = React.useRef(null);
  const [dot, setDot] = React.useState(0);

  const onScroll = () => {
    const el = deckRef.current;
    if (!el || wide || !el.children.length) return;
    const step = el.children[0].getBoundingClientRect().width + 14;
    const i = Math.round(el.scrollLeft / step);
    if (i !== dot && i >= 0 && i < DECK.length) setDot(i);
  };

  const goDot = (i) => {
    const el = deckRef.current;
    setDot(i);
    if (!el || !el.children.length) return;
    el.scrollLeft = i * (el.children[0].getBoundingClientRect().width + 14);
  };

  const deckStyle = wide
    ? { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 16, padding: "0 26px" }
    : { display: "flex", gap: 14, overflowX: "auto", scrollSnapType: "x mandatory", padding: "0 26px", scrollPaddingLeft: 26 };

  const recent = threads.slice(0, 5);

  return React.createElement("div", { style: { padding: "0 0 56px" } },
    React.createElement("div", { style: { padding: "34px 26px 26px" } },
      React.createElement("h1", { style: { fontFamily: "Barlow,sans-serif", fontWeight: 800, fontSize: 34, letterSpacing: "-.025em", color: C.navy, lineHeight: 1.05, margin: 0 } },
        greeting() + ", " + (user.firstName || "there")),
      React.createElement("div", { style: { fontSize: 13.5, color: C.mid, fontWeight: 500, marginTop: 6 } },
        new Date().toLocaleDateString("en-NZ", { weekday: "long", day: "numeric", month: "long" }))
    ),

    React.createElement("div", { ref: deckRef, onScroll: onScroll, className: "nb", style: deckStyle },
      DECK.map(c => React.createElement(ToolCard, { key: c.key, tool: c, wide, onOpen: () => (c.href ? openDoc(c) : onOpen(c.key)) }))
    ),

    !wide ? React.createElement("div", { style: { display: "flex", gap: 4, justifyContent: "center", alignItems: "center", padding: "18px 0 2px" } },
      DECK.map((c, i) => React.createElement("span", {
        key: c.key, onClick: () => goDot(i), "aria-label": "Go to " + c.title,
        style: {
          width: dot === i ? 26 : 10, height: 3, borderRadius: 999, cursor: "pointer",
          transition: "all .25s " + EASE, background: dot === i ? C.orange : "#DEDCD7",
        },
      }))
    ) : null,

    React.createElement("div", { style: { padding: "26px 26px 0" } },
      React.createElement(SweepCard, { onToast })
    ),

    recent.length ? React.createElement("div", { style: { padding: "30px 26px 0" } },
      React.createElement("div", { style: { fontSize: 10, fontWeight: 700, letterSpacing: ".16em", textTransform: "uppercase", color: C.mid, paddingBottom: 6 } },
        "Picking up where you left off"),
      React.createElement("div", { style: { display: "flex", flexDirection: "column" } },
        recent.map((t, i) => React.createElement("div", {
          key: t.id, onClick: () => onOpen(t.tool, t.id),
          style: {
            display: "flex", alignItems: "center", gap: 14, padding: "15px 0", cursor: "pointer",
            borderBottom: i === recent.length - 1 ? "none" : "1px solid #EDECE8", minHeight: 52, boxSizing: "border-box",
          },
        },
          React.createElement("span", { style: { width: 6, height: 6, borderRadius: "50%", background: (TOOLS[t.tool] || TOOLS.regs).colour, flexShrink: 0 } }),
          React.createElement("div", { style: { flex: 1, minWidth: 0 } },
            React.createElement("div", { style: { fontSize: 14, fontWeight: 600, color: C.char, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" } }, t.title),
            React.createElement("div", { style: { fontSize: 11.5, color: C.mid, fontWeight: 500, marginTop: 2 } },
              (TOOLS[t.tool] || {}).title + " - " + t.ago)),
          React.createElement(Icon, { name: "chev", size: 15, colour: "#C9C7C2", width: 2.2 })
        ))
      )
    ) : null,

    React.createElement("div", { style: { padding: "34px 26px 0", display: "flex", justifyContent: "center" } },
      React.createElement("button", {
        onClick: onBug,
        style: {
          display: "flex", alignItems: "center", gap: 8, background: "transparent", border: "none",
          minHeight: 44, padding: "0 8px", cursor: "pointer", fontFamily: "Inter,sans-serif",
          fontSize: 13, fontWeight: 600, color: C.mid,
        },
      }, React.createElement(Icon, { name: "bug", size: 15, width: 1.9 }), "Something not right? Tell Ryan")
    ),

    React.createElement("div", { style: { textAlign: "center", fontSize: 12, fontWeight: 600, color: C.mid, paddingTop: 26 } }, "Hub version " + APP_VERSION),
    React.createElement("div", { style: { textAlign: "center", fontSize: 12, color: C.mid, paddingTop: 6, lineHeight: 1.5 } }, "Questions and answers are saved so Ryan can keep improving the Hub.")
  );
}

// ============================================================
// CHAT - one thread per tool. The tool name and scope line sit in
// the header at all times so nobody has to guess what is answering.
// ============================================================
// What staff see when Anthropic has switched the AI off. Same words in the
// red banner and in the chat where the answer failed.
function aiOffText(st) {
  if (st && st.kind === "limit") {
    return "AI answers are off, the monthly usage limit has been reached. Back on " +
      (st.resets ? new Date(st.resets + "T00:00:00Z").toLocaleDateString("en-NZ", { day: "numeric", month: "long", timeZone: "Pacific/Auckland" }) : "the 1st of next month") + ".";
  }
  return "AI answers are off, the account is out of credit. Back once it's topped up.";
}

// The chat as it stood when something was reported (27 Sep 2026, Ryan).
// Sent with every "Tell Ryan" report so he can see what led up to the
// fault. Text only: photos go as a [photo] marker, never the image. The
// tool's opening greeting is dropped, the last 30 messages kept, each
// capped so one long answer can't swamp the report.
function chatTranscript(msgs, tool) {
  return (msgs || [])
    .filter(m => m && !(m.role === "assistant" && tool && m.text === tool.greet))
    .slice(-30)
    .map(m => ({
      who: m.role === "user" ? "Staff" : "Hub",
      text: String(m.text || "").slice(0, 4000),
      photo: !!m.image,
      cite: m.cite || "",
      error: !!m.isError,
      queued: !!m.queued,
      rated: m.rated || "",
    }));
}

// The line under a reviewed regs answer. "pending" only counts while that
// answer's review is actually running (a synced or reloaded chat can't
// still be waiting on one).
function reviewLine(m, reviewId, C) {
  if (!m || !m.review) return null;
  const st = (m.review === "pending" && m.id !== reviewId) ? "unchecked" : m.review;
  const look = {
    pending:   { text: "Answer is being reviewed\u2026", color: C.mid },
    ok:        { text: "Reviewed: checked against the standard", color: C.success },
    corrected: { text: "Corrected after review. This is the checked answer.", color: C.gold },
    unchecked: { text: "Not reviewed this time. Check the standard before you rely on it.", color: C.gold },
  }[st];
  if (!look) return null;
  return React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 7 } },
    React.createElement("span", { style: { width: 6, height: 6, borderRadius: "50%", background: look.color, flexShrink: 0 } }),
    React.createElement("span", { style: { fontSize: 12, fontWeight: 600, color: look.color } }, look.text));
}

function Chat({ tool, msgs, setMsgs, wide, offline, onBack, onToast, onSignedOut, onBug, onAiCheck }) {
  const [input, setInput] = React.useState("");
  const [photo, setPhoto] = React.useState(null);   // { data, thumb } waiting to send
  const fileRef = React.useRef(null);
  const pickPhoto = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    try { setPhoto(await preparePhoto(f)); }
    catch (err) { onToast("Couldn't read that photo, try another"); }
  };
  const [status, setStatus] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const scrollRef = React.useRef(null);
  const nearBottom = React.useRef(true);
  const abortRef = React.useRef(null);
  // Bumped by "New chat". Every send captures the generation it started in
  // and drops its reply if the thread has been cleared underneath it, so
  // starting fresh mid-answer can never drop the old answer (or the "that
  // didn't get through" error from its own abort) into the new thread.
  const genRef = React.useRef(0);
  // Id of the answer on screen that is still being reviewed (regs).
  const [reviewId, setReviewId] = React.useState(null);

  // componentDidUpdate timing is not reliable across the runtimes this
  // ships to, so pin explicitly after send, after reply, and after a
  // flush. rAF plus a short follow-up covers late layout.
  const pin = React.useCallback(() => {
    if (nearBottom.current === false) return;
    const go = () => { const el = scrollRef.current; if (el) el.scrollTop = el.scrollHeight; };
    requestAnimationFrame(go);
    setTimeout(go, 60);
  }, []);

  React.useEffect(() => { pin(); }, [msgs.length, pin]);
  React.useEffect(() => () => { if (abortRef.current) abortRef.current.abort(); }, []);

  const onChatScroll = (e) => {
    const el = e.target;
    nearBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  // Thumbs. Up is acknowledged locally. Down asks for a reason in one
  // extra tap, then posts to /report-bug, which files it in Bug Reports
  // and emails Ryan. It goes to Ryan, never back into the model.
  const [rating, setRating] = React.useState(null); // message id awaiting a reason

  const markRated = (id, verdict) =>
    setMsgs(prev => prev.map(m => m.id === id ? Object.assign({}, m, { rated: verdict }) : m));

  const thumbUp = (id) => { markRated(id, "up"); onToast("Cheers, noted"); };

  const thumbDown = (id) => { markRated(id, "down"); setRating(id); };

  const sendReason = (id, reason) => {
    setRating(null);
    const idx = msgs.findIndex(x => x.id === id);
    const m = idx >= 0 ? msgs[idx] : null;
    // The question that produced THIS answer, not simply the newest one, so a
    // thumbs down on an earlier answer reports the right question.
    const q = msgs.slice(0, idx >= 0 ? idx : msgs.length).reverse().find(x => x.role === "user");
    const description =
      "WRONG ANSWER reported from the " + tool.title + " tool.\n" +
      "Reason: " + reason + "\n\n" +
      "Question: " + (q ? q.text : "(none)") + "\n\n" +
      "Answer given: " + (m ? m.text : "(none)");
    api("/report-bug", {
      method: "POST",
      body: {
        description,
        context: {
          tab: tool.title,
          lastUserMessage: q ? q.text : "",
          transcript: chatTranscript(msgs.slice(0, idx >= 0 ? idx + 1 : msgs.length), tool),
          appVersion: APP_VERSION, liveVersion: LIVE_VERSION,
          userAgent: navigator.userAgent,
          url: location.href,
        },
      },
    });
    onToast("Sent to Ryan");
  };

  // A miss can be reported in one tap (24 Sep 2026). "Couldn't find it" was a
  // reason on the thumbs down, but a miss never showed the thumbs, so the one
  // case it was for could not be reported.
  const reportMiss = (id) => { markRated(id, "down"); sendReason(id, "Couldn't find it"); };

  const REASONS = ["Wrong info", "Wrong job or site", "Couldn't find it", "Wrong tool answered"];

  const send = async (textIn) => {
    const q = (textIn || input || "").trim();
    const img = textIn ? null : photo;
    if ((!q && !img) || busy) return;
    if (img && offline) { onToast("Photos need signal. It'll go when you're back on"); return; }
    nearBottom.current = true;
    setInput("");

    const clientMsgId = uid();
    const userMsg = { id: uid(), role: "user", text: q, queued: offline, clientMsgId };
    if (img) { userMsg.image = img; setPhoto(null); }
    const history = msgs.filter(m => !m.queued && m.role).concat([userMsg]);

    setMsgs(prev => prev.concat([userMsg]));
    pin();

    if (offline) {
      Outbox.add({ clientMsgId, tool: tool.key, text: q, at: Date.now() });
      onToast("In the outbox, sends when you're back on");
      return;
    }

    setBusy(true);
    setStatus("Having a look...");
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const gen = genRef.current;

    // Regs: the answer shows as soon as it is written, marked "being
    // reviewed", and is updated in place when the review comes back.
    const earlyId = uid();
    let earlyShown = false;
    const onEarly = (er) => {
      if (genRef.current !== gen) return;
      if (tool.needsCite && er.found && !er.cite && !er.webOnly) er.uncited = true;
      const m = answerMessage(er);
      m.id = earlyId;
      m.review = "pending";
      earlyShown = true;
      setReviewId(earlyId);
      setMsgs(prev => prev.concat([m]));
      pin();
    };

    try {
      const r = await askTool(tool.key, q, history, setStatus, ctrl.signal, onEarly);
      if (genRef.current !== gen) return;
      const fm = answerMessage(r);
      if (earlyShown) { fm.id = earlyId; setMsgs(prev => prev.map(m => m.id === earlyId ? fm : m)); }
      else setMsgs(prev => prev.concat([fm]));
    } catch (e) {
      if (String(e && e.message) === "SIGNED_OUT") { onSignedOut(); return; }
      if (genRef.current !== gen) return;
      if (earlyShown) { setMsgs(prev => prev.map(m => m.id === earlyId ? Object.assign({}, m, { review: "unchecked" }) : m)); return; }
      // A failed answer re-checks /ai-status straight away, so a spend
      // limit shows up here and in the banner now, not at the next poll.
      let st = null;
      try { st = onAiCheck ? await onAiCheck() : null; } catch (e2) { st = null; }
      if (genRef.current !== gen) return;
      setMsgs(prev => prev.concat([{
        id: uid(), role: "assistant",
        text: (st && st.ok === false) ? aiOffText(st) : "That didn't get through. Have another go in a moment.",
        cite: null, sources: [], acts: false, isError: true,
      }]));
    } finally {
      if (genRef.current === gen) {
        setBusy(false);
        setStatus("");
        setReviewId(null);
        abortRef.current = null;
      }
      pin();
    }
  };

  // New chat. Drops the thread back to the tool's opening line, which also
  // brings the starter chips back. The old thread is not kept: these are
  // scratch threads, one per tool, and a second copy would only confuse
  // "picking up where you left off" on the home screen.
  const newChat = () => {
    genRef.current += 1;
    if (abortRef.current) { try { abortRef.current.abort(); } catch (e) {} }
    abortRef.current = null;
    setBusy(false);
    setStatus("");
    setReviewId(null);
    setInput("");
    setRating(null);
    nearBottom.current = true;
    setMsgs([{ id: uid(), role: "assistant", text: tool.greet, cite: null, sources: [], acts: false }]);
    onToast("New chat");
  };

  const col = wide
    ? { maxWidth: 760, margin: "0 auto", display: "flex", flexDirection: "column", gap: 22 }
    : { display: "flex", flexDirection: "column", gap: 22 };

  return React.createElement("div", { style: { height: "100%", display: "flex", flexDirection: "column" } },

    React.createElement("div", { style: { flexShrink: 0, display: "flex", alignItems: "center", gap: 12, padding: "22px 26px 16px" } },
      !wide ? React.createElement("button", {
        onClick: onBack, "aria-label": "Back",
        style: { width: 44, height: 44, borderRadius: "50%", border: "none", background: C.off, color: C.navy, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, padding: 0 },
      }, React.createElement(Icon, { name: "back", size: 17, width: 2.2 })) : null,
      React.createElement("span", { style: { width: 8, height: 8, borderRadius: "50%", flexShrink: 0, background: tool.colour } }),
      React.createElement("div", { style: { flex: 1, minWidth: 0 } },
        React.createElement("div", { style: { fontFamily: "Barlow,sans-serif", fontWeight: 800, fontSize: 19, color: C.navy, letterSpacing: "-.02em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" } }, tool.title),
        React.createElement("div", { style: { fontSize: 11.5, color: C.mid, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginTop: 1 } }, tool.scope)),
      onBug ? React.createElement("button", {
        onClick: onBug, "aria-label": "Something not right? Tell Ryan", title: "Something not right? Tell Ryan",
        style: { width: 44, height: 44, borderRadius: "50%", border: "1px solid " + C.line, background: "#fff", color: C.mid, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, padding: 0 },
      }, React.createElement(Icon, { name: "bug", size: 17, width: 1.9 })) : null,
      React.createElement("button", {
        onClick: newChat, "aria-label": "New chat", title: "New chat",
        style: {
          display: "flex", alignItems: "center", gap: 7, flexShrink: 0,
          minHeight: 44, padding: wide ? "0 16px" : "0 13px", borderRadius: 999,
          border: "1px solid " + C.line, background: "#fff", color: C.navy,
          fontFamily: "Barlow,sans-serif", fontWeight: 700, fontSize: 13.5, cursor: "pointer",
        },
      },
        React.createElement(Icon, { name: "newchat", size: 16, colour: C.orange, width: 1.9 }),
        "New chat")
    ),

    offline ? React.createElement("div", { style: { flexShrink: 0, display: "flex", alignItems: "center", gap: 9, padding: "0 26px 12px" } },
      React.createElement("span", { style: { width: 6, height: 6, borderRadius: "50%", background: C.warning, flexShrink: 0 } }),
      React.createElement("div", { style: { fontSize: 12, fontWeight: 600, color: C.gold } }, "Offline. Anything you send waits in the outbox.")
    ) : null,

    React.createElement("div", {
      ref: scrollRef, onScroll: onChatScroll, className: "nb",
      style: { flex: 1, minHeight: 0, overflowY: "auto", padding: "4px 26px 10px" },
    },
      React.createElement("div", { style: col },
        msgs.map(m => m.role === "user"
          ? React.createElement("div", { key: m.id, style: { display: "flex", flexDirection: "column" } },
              React.createElement("div", { style: { alignSelf: "flex-end", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 5, maxWidth: "78%" } },
                m.image && m.image.thumb ? React.createElement("img", { src: m.image.thumb, alt: "Photo sent", style: { maxWidth: 200, maxHeight: 200, borderRadius: 14, display: "block" } }) : null,
                m.text ? React.createElement("div", { style: { background: C.navy, color: "#fff", borderRadius: "20px 20px 6px 20px", padding: "12px 16px", fontSize: 15, lineHeight: 1.45, fontWeight: 500 } }, m.text) : null,
                m.queued ? React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 6 } },
                  React.createElement("span", { style: { width: 5, height: 5, borderRadius: "50%", background: C.warning } }),
                  React.createElement("span", { style: { fontSize: 11.5, fontWeight: 600, color: C.gold } }, "In the outbox, sends when you're back on")
                ) : null
              ))
          : React.createElement("div", { key: m.id, style: { alignSelf: "flex-start", maxWidth: "94%", display: "flex", flexDirection: "column", gap: 12 } },
              React.createElement("div", { style: { fontSize: 15, lineHeight: 1.6, color: m.isError ? C.danger : C.char, whiteSpace: "pre-line" } }, renderRich(m.text, C)),
              reviewLine(m, reviewId, C),
              m.cite ? (m.ref
                ? React.createElement("button", {
                    onClick: () => openCite(m.ref, onToast), "aria-label": "Open " + m.cite,
                    style: { display: "inline-flex", alignSelf: "flex-start", alignItems: "center", gap: 6, background: "transparent", border: "none", padding: 0, minHeight: 36, cursor: "pointer", fontFamily: "Inter,sans-serif", fontSize: 12, fontWeight: 700, color: C.navy, textAlign: "left" },
                  },
                    React.createElement("span", { style: { borderBottom: "1px solid rgba(0,43,96,.35)", paddingBottom: 1 } }, "Open " + m.cite),
                    React.createElement(Icon, { name: "chev", size: 13, width: 2.2 }))
                : React.createElement("div", { style: { display: "inline-flex", alignSelf: "flex-start", alignItems: "center", gap: 8 } },
                    React.createElement("span", { style: { fontSize: 12, fontWeight: 700, color: C.navy, borderBottom: "1px solid rgba(0,43,96,.25)", paddingBottom: 1 } }, m.cite))
              ) : null,
              m.miss && !m.rated ? React.createElement("button", {
                onClick: () => reportMiss(m.id),
                style: { alignSelf: "flex-start", background: "transparent", border: "none", padding: 0, minHeight: 36, cursor: "pointer", fontFamily: "Inter,sans-serif", fontSize: 12, fontWeight: 600, color: C.mid, textDecoration: "underline" },
              }, "It should be in there, tell Ryan") : null,
              m.miss && m.rated ? React.createElement("div", { style: { fontSize: 12, fontWeight: 600, color: C.mid } }, "Sent to Ryan") : null,
              (m.sources || []).length ? React.createElement("div", { style: { display: "flex", flexDirection: "column", gap: 6 } },
                m.sources.map((s, i) => React.createElement("div", { key: i, style: { fontSize: 12.5, fontWeight: 600, color: C.navy } }, "- " + s.label))
              ) : null,
              m.acts ? React.createElement("div", { style: { display: "flex", gap: 18, paddingTop: 2, alignItems: "center" } },
                React.createElement("button", {
                  onClick: () => { try { navigator.clipboard.writeText(plainText(m.text)); onToast("Copied"); } catch { onToast("Couldn't copy"); } },
                  style: { display: "flex", alignItems: "center", gap: 7, background: "transparent", border: "none", padding: 0, minHeight: 44, cursor: "pointer", fontFamily: "Inter,sans-serif", fontSize: 12, fontWeight: 600, color: C.mid },
                }, React.createElement(Icon, { name: "copy", size: 14, width: 1.9 }), "Copy"),
                React.createElement("button", {
                  onClick: () => thumbUp(m.id), "aria-label": "Good answer",
                  style: { background: "transparent", border: "none", padding: 0, minHeight: 44, cursor: "pointer", color: m.rated === "up" ? C.success : C.mid },
                }, React.createElement(Icon, { name: "up", size: 15, width: 1.9 })),
                React.createElement("button", {
                  onClick: () => thumbDown(m.id), "aria-label": "Wrong answer",
                  style: { background: "transparent", border: "none", padding: 0, minHeight: 44, cursor: "pointer", color: m.rated === "down" ? C.danger : C.mid },
                }, React.createElement(Icon, { name: "down", size: 15, width: 1.9 }))
              ) : null,
              rating === m.id ? React.createElement("div", { style: { display: "flex", flexWrap: "wrap", gap: 8, paddingTop: 2 } },
                REASONS.map(r => React.createElement("button", {
                  key: r, onClick: () => sendReason(m.id, r),
                  style: {
                    minHeight: 44, padding: "0 14px", borderRadius: 999, border: "1px solid " + C.line,
                    background: "#fff", color: C.char, fontFamily: "Inter,sans-serif", fontSize: 13,
                    fontWeight: 600, cursor: "pointer",
                  },
                }, r))
              ) : null
            )),
        busy && !reviewId ? React.createElement("div", { style: { alignSelf: "flex-start", fontSize: 14, color: C.mid, fontWeight: 500 } }, status || "Having a look...") : null
      )
    ),

    React.createElement("div", { style: { flexShrink: 0, padding: "0 26px 22px" } },
      React.createElement("div", { style: col },
        msgs.length <= 1 ? React.createElement("div", { className: "nb", style: { display: "flex", gap: 16, overflowX: "auto", padding: "10px 0 14px" } },
          tool.chips.map(label => React.createElement("button", {
            key: label, onClick: () => send(label),
            style: { flexShrink: 0, background: "transparent", border: "none", padding: 0, minHeight: 44, fontFamily: "Inter,sans-serif", fontSize: 13, fontWeight: 600, color: C.mid, cursor: "pointer", whiteSpace: "nowrap" },
          }, label))
        ) : null,
        photo ? React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 10, padding: "0 0 10px" } },
          React.createElement("img", { src: photo.thumb, alt: "Photo to send", style: { width: 56, height: 56, objectFit: "cover", borderRadius: 10 } }),
          React.createElement("div", { style: { fontSize: 12.5, fontWeight: 600, color: C.mid, flex: 1 } }, "Photo ready. Add a question or just send it."),
          React.createElement("button", {
            onClick: () => setPhoto(null), "aria-label": "Remove photo",
            style: { width: 44, height: 44, border: "none", background: "transparent", color: C.mid, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" },
          }, React.createElement(Icon, { name: "x", size: 18, width: 2 }))
        ) : null,
        React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 6, background: C.off, borderRadius: 26, padding: tool.photo ? "7px 7px 7px 8px" : "7px 7px 7px 20px", boxSizing: "border-box" } },
          tool.photo ? React.createElement("input", { ref: fileRef, type: "file", accept: "image/*", onChange: pickPhoto, style: { display: "none" } }) : null,
          tool.photo ? React.createElement("button", {
            onClick: () => { if (fileRef.current) fileRef.current.click(); }, "aria-label": "Add a photo", disabled: busy,
            style: { width: 44, height: 44, borderRadius: "50%", border: "none", background: "transparent", color: C.navy, display: "flex", alignItems: "center", justifyContent: "center", cursor: busy ? "default" : "pointer", flexShrink: 0 },
          }, React.createElement(Icon, { name: "camera", size: 21, width: 1.9 })) : null,
          React.createElement("input", {
            value: input,
            onChange: e => setInput(e.target.value),
            onKeyDown: e => { if (e.key === "Enter") { e.preventDefault(); send(); } },
            placeholder: tool.placeholder,
            "aria-label": "Ask " + tool.title,
            style: { flex: 1, minWidth: 0, border: "none", background: "transparent", outline: "none", fontFamily: "Inter,sans-serif", fontSize: 17, color: C.char },
          }),
          React.createElement("button", {
            onClick: () => send(), "aria-label": "Send", disabled: busy,
            style: { width: 52, height: 52, borderRadius: "50%", border: "none", background: busy ? "#D9A491" : C.orange, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: busy ? "default" : "pointer", flexShrink: 0, transition: "background .15s " + EASE },
          }, React.createElement(Icon, { name: "send", size: 19, width: 2.3 }))
        )
      )
    )
  );
}

// ============================================================
// Styles + fonts. Injected from here rather than the HTML shell so
// the shell does not need a three-location sync for a font change.
// ============================================================
function injectChrome() {
  if (document.getElementById("bayside-app-chrome")) return;
  const pre1 = document.createElement("link");
  pre1.rel = "preconnect"; pre1.href = "https://fonts.googleapis.com";
  const pre2 = document.createElement("link");
  pre2.rel = "preconnect"; pre2.href = "https://fonts.gstatic.com"; pre2.crossOrigin = "anonymous";
  const font = document.createElement("link");
  font.rel = "stylesheet";
  font.href = "https://fonts.googleapis.com/css2?family=Barlow:wght@600;700;800&family=Inter:wght@400;500;600;700&display=swap";
  const st = document.createElement("style");
  st.id = "bayside-app-chrome";
  st.textContent =
    "html,body{margin:0;padding:0;height:100%;background:" + C.deep + ";}" +
    "#root{height:100%;}" +
    "*{-webkit-tap-highlight-color:transparent;}" +
    ".nb::-webkit-scrollbar{display:none}.nb{scrollbar-width:none}" +
    "@keyframes bayshake{0%,100%{transform:translateX(0)}20%{transform:translateX(-8px)}40%{transform:translateX(8px)}60%{transform:translateX(-5px)}80%{transform:translateX(5px)}}" +
    "@keyframes bayfade{from{opacity:0;transform:translateY(5px)}to{opacity:1;transform:none}}" +
    "input::placeholder{color:" + C.mid + ";}" +
    // Chrome forces its own pale autofill background on saved logins, which
    // made the navy sign-in card look half broken. Keep our own look.
    // letter-spacing normal on the placeholder: the PIN field sets .3em so the
    // typed digits space out, but that also stretched its placeholder into
    // "P I N", which reads like a typo on the sign-in screen. (15 Sep 2026)
    ".bay-login-input::placeholder{color:rgba(255,255,255,.45);letter-spacing:normal;}" +
    ".bay-login-input:-webkit-autofill,.bay-login-input:-webkit-autofill:hover,.bay-login-input:-webkit-autofill:focus{" +
      "-webkit-text-fill-color:#fff;caret-color:#fff;" +
      "-webkit-box-shadow:0 0 0 1000px rgba(255,255,255,.10) inset;" +
      "transition:background-color 9999s ease-in-out 0s;}" +
    "@media (max-width:420px){body{font-size:17px}}";
  document.head.appendChild(pre1);
  document.head.appendChild(pre2);
  document.head.appendChild(font);
  document.head.appendChild(st);
}

function relTime(ts) {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 90) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return m + " min ago";
  const h = Math.floor(m / 60);
  if (h < 24) return h === 1 ? "1 hour ago" : h + " hours ago";
  const d = Math.floor(h / 24);
  if (d === 1) return "yesterday";
  return d + " days ago";
}

// ============================================================
// ROOT
// ============================================================
function BaysideAssistant() {
  const [ready, setReady] = React.useState(false);
  const [user, setUser] = React.useState(null);
  const [screen, setScreen] = React.useState("home");
  const [toolKey, setToolKey] = React.useState("regs");
  const [threads, setThreads] = React.useState(() => LS.getJSON(THREADS_KEY, {}) || {});
  const [wide, setWide] = React.useState(typeof window !== "undefined" && window.innerWidth >= 820);
  const [offline, setOffline] = React.useState(typeof navigator !== "undefined" && navigator.onLine === false);
  const [toast, setToast] = React.useState("");
  const [bugOpen, setBugOpen] = React.useState(false);
  // AI quota banner (27 Sep 2026, Ryan). Polls the worker's /ai-status on
  // mount and every 5 min. When Anthropic has switched the Hub's AI off,
  // /ai-status returns { ok:false, kind:"limit"|"credit", resets } and a red
  // banner sits across every screen so the team knows why answers fail.
  // "limit" = monthly usage limit, back on the resets date (UTC midnight).
  // "credit" = account out of credit, back once it is topped up.
  // While a banner (AI off, or new version) is up, the banner strip carries
  // the notch (safe-area) padding and the phone top bar drops its own.
  const [aiStatus, setAiStatus] = React.useState({ ok: true });
  const toastTimer = React.useRef(null);

  React.useEffect(() => { injectChrome(); }, []);

  const checkAi = React.useCallback(async () => {
    try {
      const r = await fetch(WORKER_URL + "/ai-status", { cache: "no-store" });
      const d = await r.json();
      if (d && typeof d.ok === "boolean") { setAiStatus(d); return d; }
    } catch (e) { /* keep previous state on a network blip */ }
    return null;
  }, []);

  // New version check (27 Sep 2026, Ryan). A phone that keeps the Hub open
  // keeps running the old code until it reloads. Compare with /version.json
  // on open, every 5 min and whenever the app comes back to the front; if
  // Pages has something newer, show a bar that reloads onto it.
  const [liveVersion, setLiveVersion] = React.useState("");
  React.useEffect(() => {
    const checkVersion = async () => {
      try {
        const r = await fetch("/version.json?nc=" + Date.now(), { cache: "no-store" });
        if (!r.ok) return;
        const d = await r.json();
        if (d && typeof d.v === "string" && /^\d{4}\.\d{2}\.\d{2}(\.\d+)?$/.test(d.v)) { LIVE_VERSION = d.v; setLiveVersion(d.v); }
      } catch (e) { /* offline or no file yet: say nothing */ }
    };
    const onVis = () => { if (document.visibilityState === "visible") checkVersion(); };
    checkVersion();
    const t = setInterval(checkVersion, 5 * 60 * 1000);
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, []);
  const updateReady = !!liveVersion && liveVersion !== APP_VERSION;
  const topBanner = !aiStatus.ok || updateReady;

  React.useEffect(() => {
    checkAi();
    const t = setInterval(checkAi, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [checkAi]);

  const showToast = React.useCallback((t) => {
    clearTimeout(toastTimer.current);
    setToast(t);
    toastTimer.current = setTimeout(() => setToast(""), 2200);
  }, []);

  // Session check. A stale token means the sign-in screen, not a
  // half-working app that 403s on every question.
  React.useEffect(() => {
    let live = true;
    (async () => {
      if (!token()) { if (live) { setReady(true); } return; }
      // /validate-token is POST ONLY and answers { valid, user:{name,role}, exp }.
      // Calling it with GET, or reading res.data.name, silently failed the check
      // and wiped a perfectly good token, so every reload signed the user out
      // (caught in smoke testing, 14 Sep 2026).
      const res = await api("/validate-token", { method: "POST", body: {} });
      if (!live) return;
      if (res.ok && res.data && res.data.valid) {
        const u = res.data.user || {};
        const nm = (u.name || LS.get(LAST_NAME_KEY, "") || "").trim();
        setUser({ name: nm, firstName: nm.split(/\s+/)[0] || "there", role: u.role || "staff" });
      } else if (res.status === 401 || res.status === 403) {
        // Only clear on an actual rejection. A network blip must not log a tech
        // out on site; leave the token alone and let them retry.
        LS.remove(SESSION_TOKEN_KEY);
      }
      setReady(true);
    })();
    return () => { live = false; };
  }, []);

  React.useEffect(() => {
    const onResize = () => setWide(window.innerWidth >= 820);
    const goOn = () => setOffline(false);
    const goOff = () => setOffline(true);
    window.addEventListener("resize", onResize);
    window.addEventListener("online", goOn);
    window.addEventListener("offline", goOff);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("online", goOn);
      window.removeEventListener("offline", goOff);
    };
  }, []);

  React.useEffect(() => { LS.setJSON(THREADS_KEY, stripPhotos(threads)); }, [threads]);

  const setMsgs = React.useCallback((updater) => {
    setThreads(prev => {
      const cur = prev[toolKey] || { msgs: [], at: Date.now() };
      const next = typeof updater === "function" ? updater(cur.msgs) : updater;
      const first = next.find(m => m.role === "user");
      return Object.assign({}, prev, {
        [toolKey]: { msgs: next, at: Date.now(), title: first ? first.text : TOOLS[toolKey].title },
      });
    });
  }, [toolKey]);

  const openTool = (k, _threadId) => {
    setToolKey(k);
    setThreads(prev => {
      if (prev[k] && prev[k].msgs && prev[k].msgs.length) return prev;
      return Object.assign({}, prev, {
        [k]: { msgs: [{ id: uid(), role: "assistant", text: TOOLS[k].greet, cite: null, sources: [], acts: false }], at: Date.now(), title: TOOLS[k].title },
      });
    });
    setScreen("chat");
  };

  // Outbox flush. Sends in the order they were queued, one at a time,
  // and only removes an item once the server has answered, so a drop
  // mid-flush retries rather than losing the question.
  React.useEffect(() => {
    if (offline) return;
    let cancelled = false;
    (async () => {
      const queue = Outbox.all();
      if (!queue.length) return;
      showToast(queue.length === 1 ? "Outbox sent, 1 question" : "Outbox sent, " + queue.length + " questions");
      for (const item of queue) {
        if (cancelled) return;
        const tool = TOOLS[item.tool];
        if (!tool) { Outbox.remove(item.clientMsgId); continue; }
        try {
          const hist = ((threads[item.tool] || {}).msgs || []).filter(m => m.role);
          const r = await askTool(item.tool, item.text, hist.concat([{ role: "user", text: item.text }]), () => {}, undefined);
          setThreads(prev => {
            const cur = prev[item.tool] || { msgs: [] };
            const msgs = cur.msgs.map(m => m.clientMsgId === item.clientMsgId ? Object.assign({}, m, { queued: false }) : m);
            msgs.push(answerMessage(r));
            return Object.assign({}, prev, { [item.tool]: Object.assign({}, cur, { msgs, at: Date.now() }) });
          });
          Outbox.remove(item.clientMsgId);
        } catch (e) {
          if (String(e && e.message) === "SIGNED_OUT") { signOut(); return; }
          return; // leave it queued, try again next time we reconnect
        }
      }
    })();
    return () => { cancelled = true; };
  }, [offline]); // eslint-disable-line

  const signOut = () => {
    LS.remove(SESSION_TOKEN_KEY);
    setUser(null);
    setScreen("home");
    showToast("Signed out, sign in again");
  };

  const recents = Object.keys(threads)
    .filter(k => TOOLS[k] && (threads[k].msgs || []).some(m => m.role === "user"))
    .map(k => ({ id: k, tool: k, title: threads[k].title || TOOLS[k].title, at: threads[k].at || 0, ago: relTime(threads[k].at || Date.now()) }))
    .sort((a, b) => b.at - a.at);

  if (!ready) {
    return React.createElement("div", { style: { height: "100%", background: C.deep } });
  }

  if (!user) {
    return React.createElement("div", {
      style: { position: "absolute", inset: 0, overflow: "hidden", background: "linear-gradient(180deg," + C.navy + " 0%," + C.deep + " 100%)", fontFamily: "Inter,system-ui,sans-serif" },
    }, React.createElement(Login, {
      // /login answers { token, user:{name,role}, exp }. The registry's full
      // name is the one to keep, not whatever they typed into the box.
      onSignedIn: (d) => {
        const u = (d && d.user) || {};
        const nm = (u.name || LS.get(LAST_NAME_KEY, "") || "").trim();
        if (u.name) LS.set(LAST_NAME_KEY, u.name);
        setUser({ name: nm, firstName: nm.split(/\s+/)[0] || "there", role: u.role || "staff" });
      },
    }));
  }

  const tool = TOOLS[toolKey];
  const msgs = (threads[toolKey] || {}).msgs || [];

  return React.createElement("div", {
    style: { position: "absolute", inset: 0, overflow: "hidden", background: "linear-gradient(180deg," + C.navy + " 0%," + C.deep + " 100%)", fontFamily: "Inter,system-ui,sans-serif", display: "flex", flexDirection: "column" },
  },
    topBanner ? React.createElement("div", {
      style: { flexShrink: 0, paddingTop: "env(safe-area-inset-top,0px)", background: !aiStatus.ok ? "#B00020" : C.deep },
    },
      !aiStatus.ok ? React.createElement("div", {
        style: {
          background: "#B00020", color: "#fff", padding: "10px 16px",
          fontFamily: "Inter,sans-serif", fontSize: 13, fontWeight: 600,
          lineHeight: 1.4, textAlign: "center",
        },
      }, aiOffText(aiStatus)) : null,
      updateReady ? React.createElement("button", {
        onClick: () => { try { location.reload(); } catch (e) {} },
        style: {
          display: "block", width: "100%", border: "none", cursor: "pointer",
          background: C.orange, color: "#fff", padding: "10px 16px", minHeight: 44,
          fontFamily: "Inter,sans-serif", fontSize: 13, fontWeight: 700,
          lineHeight: 1.4, textAlign: "center",
        },
      }, "New version of the Hub is ready (" + liveVersion + "). Tap here to update.") : null
    ) : null,
    React.createElement("div", { style: { flex: 1, minHeight: 0, display: "flex" } },

      wide ? React.createElement("div", { style: { width: 264, flexShrink: 0, display: "flex", flexDirection: "column", padding: "28px 0 22px" } },
        React.createElement("div", {
          onClick: () => setScreen("home"),
          style: { display: "flex", alignItems: "center", gap: 11, padding: "0 26px 30px", cursor: "pointer" },
        },
          React.createElement("div", { style: { width: 32, height: 32, borderRadius: 10, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center" } },
            React.createElement("img", { src: "bayside-symbol.png", alt: "", style: { width: 21, height: "auto" } })),
          React.createElement("div", { style: { fontFamily: "Barlow,sans-serif", fontWeight: 800, fontSize: 17, color: "#fff" } }, "Bayside Hub")
        ),
        React.createElement("div", { style: { fontSize: 10, fontWeight: 700, letterSpacing: ".16em", textTransform: "uppercase", color: "rgba(255,255,255,.3)", padding: "0 26px 12px" } }, "Tools"),
        React.createElement("div", { style: { display: "flex", flexDirection: "column" } },
          ORDER.map(k => {
            const on = screen === "chat" && toolKey === k;
            return React.createElement("div", {
              key: k, onClick: () => openTool(k),
              style: { display: "flex", alignItems: "center", gap: 12, padding: "14px 26px", cursor: "pointer", position: "relative", minHeight: 52, boxSizing: "border-box", background: on ? "rgba(255,255,255,.06)" : "transparent" },
            },
              React.createElement("span", { style: { position: "absolute", left: 0, top: 8, bottom: 8, width: 2, borderRadius: "0 2px 2px 0", background: on ? C.orange : "transparent" } }),
              React.createElement("span", { style: { width: 7, height: 7, borderRadius: "50%", flexShrink: 0, background: TOOLS[k].colour } }),
              React.createElement("span", { style: { fontSize: 15, fontWeight: 600, color: on ? "#fff" : "rgba(255,255,255,.62)" } }, TOOLS[k].title)
            );
          }),
          React.createElement("div", {
            key: "__handbook", onClick: () => openDoc(HANDBOOK),
            style: { display: "flex", alignItems: "center", gap: 12, padding: "14px 26px", cursor: "pointer", position: "relative", minHeight: 52, boxSizing: "border-box", background: "transparent" },
          },
            React.createElement("span", { style: { width: 7, height: 7, borderRadius: "50%", flexShrink: 0, background: HANDBOOK.colour } }),
            React.createElement("span", { style: { fontSize: 15, fontWeight: 600, color: "rgba(255,255,255,.62)" } }, HANDBOOK.title)
          ),
          // Admin lives with the tools rather than behind a gear, so approving a
          // request or resetting a PIN is one tap. Admin role only. (15 Sep 2026)
          user.role === "admin" ? React.createElement("div", {
            key: "__admin", onClick: () => setScreen("admin"),
            style: { display: "flex", alignItems: "center", gap: 12, padding: "14px 26px", cursor: "pointer", position: "relative", minHeight: 52, boxSizing: "border-box", background: screen === "admin" ? "rgba(255,255,255,.06)" : "transparent" },
          },
            React.createElement("span", { style: { position: "absolute", left: 0, top: 8, bottom: 8, width: 2, borderRadius: "0 2px 2px 0", background: screen === "admin" ? C.orange : "transparent" } }),
            React.createElement("span", { style: { width: 7, height: 7, borderRadius: "50%", flexShrink: 0, background: C.mid } }),
            React.createElement("span", { style: { fontSize: 15, fontWeight: 600, color: screen === "admin" ? "#fff" : "rgba(255,255,255,.62)" } }, "Admin")
          ) : null
        ),
        React.createElement("div", { style: { marginTop: "auto", padding: "0 26px", display: "flex", flexDirection: "column", gap: 14 } },
          React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 9 } },
            React.createElement("span", { style: { width: 7, height: 7, borderRadius: "50%", background: offline ? C.warning : C.success } }),
            React.createElement("span", { style: { fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,.5)" } }, offline ? "Offline" : "Online")),
          React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 10, paddingTop: 14, borderTop: "1px solid rgba(255,255,255,.09)" } },
            React.createElement("div", { style: { width: 30, height: 30, borderRadius: "50%", background: C.orange, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Barlow,sans-serif", fontWeight: 700, fontSize: 12 } },
              (user.name || "?").split(/\s+/).map(w => w[0]).join("").slice(0, 2).toUpperCase()),
            React.createElement("div", { style: { fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,.8)", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } }, user.name || "Signed in"))
        )
      ) : null,

      React.createElement("div", { style: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column" } },

        !wide ? React.createElement("div", { style: { flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", padding: (topBanner ? "14px" : "calc(env(safe-area-inset-top,0px) + 22px)") + " 22px 18px" } },
          React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 10 } },
            React.createElement("div", { style: { width: 28, height: 28, borderRadius: 9, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center" } },
              React.createElement("img", { src: "bayside-symbol.png", alt: "", style: { width: 19, height: "auto" } })),
            React.createElement("div", { style: { fontFamily: "Barlow,sans-serif", fontWeight: 800, fontSize: 15.5, color: "#fff" } }, "Bayside Hub")),
          React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 14 } },
            // On a phone there is no sidebar, so Admin needs its own way in.
            // Admin role only. (15 Sep 2026)
            user.role === "admin" ? React.createElement("button", {
              onClick: () => setScreen(screen === "admin" ? "home" : "admin"),
              style: {
                minHeight: 34, padding: "0 12px", borderRadius: 999, border: "1px solid rgba(255,255,255,.22)",
                background: screen === "admin" ? C.orange : "transparent", color: "#fff",
                fontFamily: "Barlow,sans-serif", fontWeight: 700, fontSize: 12, cursor: "pointer",
              },
            }, "Admin") : null,
            React.createElement("div", { style: { display: "flex", alignItems: "center", gap: 7 } },
              React.createElement("span", { style: { width: 7, height: 7, borderRadius: "50%", background: offline ? C.warning : C.success } }),
              React.createElement("span", { style: { fontSize: 11.5, fontWeight: 600, color: "rgba(255,255,255,.5)" } }, offline ? "Offline" : "Online")),
            React.createElement("div", { style: { width: 30, height: 30, borderRadius: "50%", background: C.orange, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Barlow,sans-serif", fontWeight: 700, fontSize: 12 } },
              (user.name || "?").split(/\s+/).map(w => w[0]).join("").slice(0, 2).toUpperCase()))
        ) : null,

        React.createElement("div", {
          className: "nb",
          style: { flex: 1, minHeight: 0, background: "#fff", overflowY: "auto", position: "relative", borderRadius: wide ? "28px 0 0 0" : "28px 28px 0 0" },
        },
          screen === "admin"
            ? React.createElement("div", { style: { padding: wide ? "34px 44px 44px" : "26px 22px 40px" } },
                React.createElement("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, paddingBottom: 18 } },
                  React.createElement("h1", { style: { fontFamily: "Barlow,sans-serif", fontWeight: 800, fontSize: 28, letterSpacing: "-.025em", color: C.navy } }, "Admin"),
                  React.createElement("button", {
                    onClick: () => setScreen("home"),
                    style: { minHeight: 44, padding: "0 16px", borderRadius: 999, border: "none", background: C.line, color: C.char, fontFamily: "Barlow,sans-serif", fontWeight: 700, fontSize: 13.5, cursor: "pointer" },
                  }, "Back")),
                React.createElement(AdminPanel, { onToast: showToast }))
          : screen === "home"
            ? React.createElement(Home, { user, wide, threads: recents, onOpen: openTool, onToast: showToast, onBug: () => setBugOpen(true) })
            : React.createElement(Chat, {
                tool, msgs, setMsgs, wide, offline,
                onBack: () => setScreen("home"),
                onToast: showToast,
                onSignedOut: signOut,
                onBug: () => setBugOpen(true),
                onAiCheck: checkAi,
              })
        )
      )
    ),

    toast ? React.createElement("div", {
      style: { position: "absolute", left: 0, right: 0, bottom: 46, display: "flex", justifyContent: "center", zIndex: 80, pointerEvents: "none" },
    }, React.createElement("div", {
      style: { background: C.deep, color: "#fff", borderRadius: 999, padding: "10px 20px", fontSize: 13, fontWeight: 600, boxShadow: "0 12px 32px rgba(0,43,96,.22)" },
    }, toast)) : null,

    bugOpen ? React.createElement(BugSheet, {
      screen: screen === "chat" ? tool.title : "Home",
      transcript: screen === "chat" ? chatTranscript(msgs, tool) : null,
      onClose: () => setBugOpen(false),
      onToast: showToast,
    }) : null
  );
}

// ============================================================
// BugSheet - plain description straight to /report-bug, which files
// it in Bug Reports and emails Ryan. No AI triage in the way. Opened from
// a chat, the chat itself goes with it so Ryan sees what happened.
// ============================================================
function BugSheet({ screen, transcript, onClose, onToast }) {
  const [text, setText] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const send = async () => {
    const d = text.trim();
    if (!d || busy) return;
    setBusy(true);
    const res = await api("/report-bug", {
      method: "POST",
      body: { description: d, context: { tab: screen, transcript: transcript || [], appVersion: APP_VERSION, liveVersion: LIVE_VERSION, userAgent: navigator.userAgent, url: location.href } },
    });
    setBusy(false);
    onClose();
    onToast(res.ok ? "Sent to Ryan" : "Couldn't send that, try again");
  };

  return React.createElement("div", {
    onClick: onClose,
    style: { position: "absolute", inset: 0, background: "rgba(0,37,63,.55)", zIndex: 90, display: "flex", alignItems: "flex-end", justifyContent: "center" },
  },
    React.createElement("div", {
      onClick: e => e.stopPropagation(),
      style: { width: "100%", maxWidth: 560, boxSizing: "border-box", background: "#fff", borderRadius: "22px 22px 0 0", padding: "26px 26px calc(env(safe-area-inset-bottom,0px) + 26px)", display: "flex", flexDirection: "column", gap: 16 },
    },
      React.createElement("div", { style: { fontFamily: "Barlow,sans-serif", fontWeight: 800, fontSize: 21, color: C.navy, letterSpacing: "-.02em" } }, "What's not working?"),
      React.createElement("textarea", {
        value: text, onChange: e => setText(e.target.value), rows: 4, autoFocus: true,
        placeholder: "Tell Ryan what you were trying to do and what happened.",
        style: { width: "100%", boxSizing: "border-box", border: "1px solid " + C.line, borderRadius: 12, padding: 14, fontSize: 17, fontFamily: "Inter,sans-serif", color: C.char, resize: "vertical", outline: "none" },
      }),
      (transcript && transcript.length) ? React.createElement("div", { style: { fontSize: 13, color: C.mid, lineHeight: 1.5, marginTop: -4 } },
        "Your " + screen + " chat goes with this so Ryan can see what happened.") : null,
      React.createElement("div", { style: { fontSize: 12, color: C.mid, marginTop: -8 } }, "Hub version " + APP_VERSION),
      React.createElement("div", { style: { display: "flex", gap: 10 } },
        React.createElement("button", {
          onClick: onClose,
          style: { flex: 1, minHeight: 52, borderRadius: 999, border: "1px solid " + C.line, background: "#fff", color: C.char, fontFamily: "Barlow,sans-serif", fontWeight: 700, fontSize: 16, cursor: "pointer" },
        }, "Cancel"),
        React.createElement("button", {
          onClick: send, disabled: busy || !text.trim(),
          style: { flex: 2, minHeight: 52, borderRadius: 999, border: "none", background: (busy || !text.trim()) ? "#D9A491" : C.orange, color: "#fff", fontFamily: "Barlow,sans-serif", fontWeight: 700, fontSize: 16, cursor: (busy || !text.trim()) ? "default" : "pointer" },
        }, busy ? "Sending..." : "Send it in")
      )
    )
  );
}

// Mount
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(React.createElement(BaysideAssistant));
